import Material from '../materials/Material.js';
import Transform from '../math/Transform.js';
import MathUtils from '../math/MathUtils.js';
import SunVector from '../utils/SunVector.js';
import AmbientOcclusion from '../ao/AmbientOcclusion.js';
import DOMUtils from '../utils/DOMUtils.js';

/**
 * Represents a cuboid in the isometric world
 */
export default class Cuboid {
  // Multi-selection support
  static selectedFaces = new Set(); // Set of "cuboidKey|face" strings
  static primarySelectedFace = null; // Most recently clicked face (string)
  static selectionVersion = 0; // Incremented when selection changes, for cache invalidation

  // Legacy single selection (for backward compatibility)
  static selectedFace = null; // { cuboidKey: string, face: string }

  // Static face normals (world space) - used for shading calculations
  static FACE_NORMALS = {
    top: { x: 0, y: 1, z: 0 },      // Up (Y+)
    bottom: { x: 0, y: -1, z: 0 },  // Down (Y-)
    front: { x: 0, y: 0, z: 1 },    // Forward (Z+) - toward camera
    back: { x: 0, y: 0, z: -1 },    // Backward (Z-) - away from camera
    right: { x: 1, y: 0, z: 0 },    // Right (X+)
    left: { x: -1, y: 0, z: 0 }     // Left (X-)
  }
  
  /**
   * Create a new cuboid
   * @param {Object} options - Cuboid options
   * @param {number} options.x - X coordinate (horizontal position)
   * @param {number} options.y - Y coordinate (height position)
   * @param {number} options.z - Z coordinate (depth position)
   * @param {number} options.width - Width (X-axis)
   * @param {number} options.height - Height (Y-axis)
   * @param {number} options.depth - Depth (Z-axis)
   * @param {number} options.cubeSize - Base cube size in pixels
   * @param {Object} options.materials - Materials for each face
   */
  constructor(options) {
    // Standard coordinate system
    this.x = options.x || 0;      // Horizontal position
    this.y = options.y || 0;      // Height position
    this.z = options.z || 0;      // Depth position
    
    this.width = options.width || 1;   // Size along X-axis
    this.height = options.height || 1; // Size along Y-axis
    this.depth = options.depth || 1;   // Size along Z-axis
    this.cubeSize = options.cubeSize || 60;
    
    // Track the last camera state to detect changes
    this.lastCameraState = {
      angle: -1,
      skewAngle: -1,
      position: { x: 0, y: 0, z: 0 },
      zoomLevel: -1
    };
    
    // Track the last quadrant to detect changes
    this.lastQuadrant = -1;

    // Track if viewing from below
    this.lastViewingFromBelow = false;
    
    // Flag to indicate if this cuboid needs a full update
    this.needsUpdate = true;
    
    // Flag to indicate if this cuboid is selected
    this.isSelected = false;

    // Last selected face
    this.lastSelectedFace = null;

    // Track selection version for group highlight updates
    this.lastSelectionVersion = -1;

    // Hidden faces for split fragments (faces at split boundaries)
    // e.g., ['left'] means don't render the left face
    this.hiddenFaces = options.hiddenFaces || [];

    // Occluded faces (faces fully contained by neighbor faces)
    // Calculated by FaceOcclusion system, separate from hiddenFaces
    this.occludedFaces = options.occludedFaces || new Set();

    // Hidden edges for split fragments (edges of visible faces at split boundaries)
    // e.g., { top: ['right'], front: ['right'] } means no border on right edge of top and front faces
    this.hiddenEdges = options.hiddenEdges || {};

    // Fragment tracking for k-d tree subdivision
    this.isFragment = options.isFragment || false;
    this.parentKey = options.parentKey || null;

    // Group face tracking - propagated from parent to fragments so refreshMaterials()
    // knows to skip group-managed faces (prevents single-face texture overwrite)
    this.faceGroups = options.faceGroups || null;

    // Texture origin for seamless tiling across fragments
    // Stores the original cuboid's origin AND dimensions so materials can tile consistently
    this.textureOrigin = options.textureOrigin || {
      x: this.x,
      y: this.y,
      z: this.z,
      width: this.width,
      height: this.height,
      depth: this.depth
    };

    // Skip AO for debug/overlay cuboids (e.g., character collider visualization)
    this.skipAO = options.skipAO || false;

    // Non-collision flag - cuboid doesn't block movement (e.g., water, gas)
    // Can be set directly or determined by material properties
    this.noCollision = options.noCollision || false;

    // Set up materials (if not provided, use default colors)
    this.setupMaterials(options.materials);

    // Create DOM element structure for the cuboid
    this.element = this.createElement();
    
    // Store references to face elements (null for hidden/occluded faces)
    this.faceElements = {};
    const allFaces = ['top', 'right', 'left', 'front', 'back', 'bottom'];
    allFaces.forEach(face => {
      const isHidden = this.hiddenFaces.includes(face);
      const isOccluded = this.occludedFaces.has(face);
      this.faceElements[face] = (isHidden || isOccluded)
        ? null
        : this.element.querySelector(`.face.${face}`);
    });
    
    // Cache for computed transforms
    this.computedTransforms = {};

    // Cache for expensive atan2 calculations
    this.cachedAngles = null;

    // Cache for brightness calculations (sun shading)
    this.cachedBrightness = null;
    this.lastSunKey = null; // Track sun vector changes
  }

  /**
   * Set up materials for each face.
   * If a material isn't provided for a face, a default color material is created.
   * @param {Object} materials - Materials for each face
   */
  setupMaterials(materials = {}) {
    const defaultColors = {
      top: '#4CAF50',
      right: '#9C27B0',
      left: '#F44336',
      front: '#2196F3',
      back: '#FFC107',
      bottom: '#795548'
    };

    this.materials = {
      top: materials?.top || Material.color(defaultColors.top),
      right: materials?.right || Material.color(defaultColors.right),
      left: materials?.left || Material.color(defaultColors.left),
      front: materials?.front || Material.color(defaultColors.front),
      back: materials?.back || Material.color(defaultColors.back),
      bottom: materials?.bottom || Material.color(defaultColors.bottom)
    };
  }

  /**
   * Create the DOM element for this cuboid and initialize its faces.
   * @return {HTMLElement} The cuboid element.
   */
  createElement() {
    const cube = document.createElement('div');
    cube.className = 'cube';
    cube.dataset.width = this.width;
    cube.dataset.height = this.height;
    cube.dataset.depth = this.depth;

    // Create face elements in a fixed order.
    // Zoom is applied via CSS scale on #world, so materials use zoom=1
    const faces = ['top', 'right', 'left', 'front', 'back', 'bottom'];
    faces.forEach(face => {
      // Skip hidden faces (for split fragments at split boundaries)
      if (this.hiddenFaces.includes(face)) {
        return;
      }
      // Skip occluded faces (fully contained by neighbor faces)
      if (this.occludedFaces.has(face)) {
        return;
      }
      const faceEl = document.createElement('div');
      faceEl.className = `face ${face}`;
      // For side faces, set a default transform origin.
      if (['right', 'left', 'front', 'back'].includes(face)) {
        faceEl.style.transformOrigin = 'left top';
      }
      // Apply material: group triplanar for group-managed faces, single-face for others
      const groupId = this.faceGroups && this.faceGroups[face];
      const groupManager = groupId ? window.game?.world?.materialGroupManager : null;

      this.materials[face].apply(faceEl, 1); // Zoom applied via CSS scale on #world
      this.applyHiddenEdges(faceEl, face);

      if (groupManager) {
        // Fragment with group-managed face: apply triplanar texture directly
        // This avoids single-face texture flash on subdivision
        groupManager.applyTriplanarToFace(faceEl, this, face, groupId);
      } else {
        // Single-face path: apply per-face offsets for seamless fragment tiling
        this.applyTextureOffset(faceEl, face, 1);
        this.applyVideoOffset(faceEl, face, 1);
        this.applyGradientOffset(faceEl, face, 1);
        this.applyTextOffset(faceEl, face, 1);
        this.applySpecialEffectOffset(faceEl, face, 1);
      }
      cube.appendChild(faceEl);
    });
    return cube;
  }

  /**
   * Rebuild face elements when occlusion state changes.
   * Dynamically adds/removes face DOM elements without recreating the entire cuboid.
   */
  rebuildFaces() {
    const allFaces = ['top', 'right', 'left', 'front', 'back', 'bottom'];

    allFaces.forEach(face => {
      const isHidden = this.hiddenFaces.includes(face);
      const isOccluded = this.occludedFaces.has(face);
      const shouldExist = !isHidden && !isOccluded;
      const exists = this.faceElements[face] !== null;

      if (shouldExist && !exists) {
        // Create face that was previously hidden/occluded
        const faceEl = document.createElement('div');
        faceEl.className = `face ${face}`;
        if (['right', 'left', 'front', 'back'].includes(face)) {
          faceEl.style.transformOrigin = 'left top';
        }
        const effectiveZoom = DOMUtils.getEffectiveZoom(this.currentZoom || 1);

        // Check if face belongs to a MaterialGroup
        const groupManager = window.game?.world?.materialGroupManager;
        const group = groupManager?.getGroupForFace(this.getKey(), face);

        if (group && group.material) {
          // Face is in a MaterialGroup - use group material and triplanar settings
          group.material.apply(faceEl, effectiveZoom);

          // Apply type-specific triplanar settings
          const materialType = group.material.type;
          switch (materialType) {
            case 'texture':
              groupManager.applyTextureTriplanar(faceEl, this, face, group);
              break;
            case 'video':
              groupManager.applyVideoTriplanar(faceEl, this, face, group);
              break;
            case 'gradient':
              groupManager.applyGradientTriplanar(faceEl, this, face, group);
              break;
            case 'pattern':
              groupManager.applyPatternTriplanar(faceEl, this, face, group);
              break;
            case 'color':
              faceEl.style.opacity = group.colorOpacity;
              break;
            case 'custom':
            case 'special':
              groupManager.applySpecialTriplanar(faceEl, this, face, group);
              break;
            case 'text':
              groupManager.applyTextTriplanar(faceEl, this, face, group);
              break;
            default:
              groupManager.applyGenericTriplanar(faceEl, this, face, group);
              break;
          }

          // Apply text triplanar if material has text content (text can be on any material type)
          if (group.material.text && materialType !== 'text') {
            groupManager.applyTextTriplanar(faceEl, this, face, group);
          }
        } else {
          // Face is not in a group - use cuboid's own material and offsets
          this.materials[face].apply(faceEl, effectiveZoom);
          this.applyTextureOffset(faceEl, face, effectiveZoom);
          this.applyVideoOffset(faceEl, face, effectiveZoom);
          this.applyGradientOffset(faceEl, face, effectiveZoom);
          this.applyTextOffset(faceEl, face, effectiveZoom);
          this.applySpecialEffectOffset(faceEl, face, effectiveZoom);
        }

        this.applyHiddenEdges(faceEl, face);

        // Just append the face - the update() method will reorder faces
        // based on camera angle when needsUpdate is true
        this.element.appendChild(faceEl);
        this.faceElements[face] = faceEl;
      } else if (!shouldExist && exists) {
        // Remove face that is now hidden/occluded
        this.faceElements[face].remove();
        this.faceElements[face] = null;
        // Clear overlay references so they get re-applied when face is restored
        if (this._aoOverlays && this._aoOverlays[face]) {
          this._aoOverlays[face] = null;
        }
        if (this._shadingOverlays && this._shadingOverlays[face]) {
          this._shadingOverlays[face] = null;
        }
      }
    });

    // Force transform update on next render
    this.needsUpdate = true;
  }

  /**
   * Update materials for one or more faces.
   * @param {Object} materials - Materials to update.
   */
  updateMaterials(materials) {
    // On non-Android: zoom is applied via CSS scale on #world, so effectiveZoom=1
    // On Android: zoom is baked into dimensions, so effectiveZoom=actualZoom
    // Use effectiveZoom for all materials so borders/offsets are correct immediately
    const zoom = this.currentZoom || window.game?.world?.camera?.zoomLevel || 1;
    const effectiveZoom = DOMUtils.getEffectiveZoom(zoom);

    Object.entries(materials).forEach(([face, material]) => {
      if (this.materials[face]) {
        // Clean up old material's audio source before replacing
        // But skip if the old material is shared by a group (group manages its own audio lifecycle)
        const oldMaterial = this.materials[face];
        if (oldMaterial._cleanupAudioSource && !oldMaterial._isGroupMaterial) {
          oldMaterial._cleanupAudioSource();
        }
        this.materials[face] = material;
        const faceEl = this.faceElements[face];
        if (faceEl) {
          // Preserve face dimensions before clearing styles (needed for pixel-based gradients)
          const faceWidth = faceEl.style.width;
          const faceHeight = faceEl.style.height;

          // Preserve overlays before clearing innerHTML
          const aoOverlay = faceEl.querySelector('.ao-overlay');
          const shadowOverlay = faceEl.querySelector('.character-shadow-overlay');
          const shadingOverlay = faceEl.querySelector('.shading-overlay');

          faceEl.style.cssText = '';
          faceEl.innerHTML = '';
          faceEl.className = `face ${face}`;

          // Restore preserved overlays
          if (aoOverlay) faceEl.appendChild(aoOverlay);
          if (shadowOverlay) faceEl.appendChild(shadowOverlay);
          if (shadingOverlay) faceEl.appendChild(shadingOverlay);

          // Restore face dimensions so material.apply can read them
          if (faceWidth) faceEl.style.width = faceWidth;
          if (faceHeight) faceEl.style.height = faceHeight;

          if (['right', 'left', 'front', 'back'].includes(face)) {
            faceEl.style.transformOrigin = 'left top';
          }

          material.apply(faceEl, effectiveZoom);
          // Apply texture offset for seamless fragment tiling
          this.applyTextureOffset(faceEl, face, effectiveZoom);
          // Remove borders at split boundaries
          this.applyHiddenEdges(faceEl, face);
          // Apply video offset for seamless video display
          this.applyVideoOffset(faceEl, face, effectiveZoom);
          // Apply gradient offset for seamless gradient display
          this.applyGradientOffset(faceEl, face, effectiveZoom);
          // Apply text offset for seamless text display
          this.applyTextOffset(faceEl, face, effectiveZoom);
          // Apply special effect offset for seamless metallic/holographic display
          this.applySpecialEffectOffset(faceEl, face, effectiveZoom);
        }
      }
    });

    // Force an update after changing materials
    this.needsUpdate = true;
  }

  /**
   * Refresh all materials.
   * On Android: uses actual zoom (baked into dimensions).
   * On other platforms: uses zoom=1 (CSS scale on #world handles zoom).
   */
  refreshMaterials() {
    // Get effective zoom: actual zoom on Android, 1 on other platforms
    const effectiveZoom = DOMUtils.getEffectiveZoom(this.currentZoom || 1);
    Object.entries(this.materials).forEach(([face, material]) => {
      const faceEl = this.faceElements[face];
      if (faceEl && material) {
        // Skip faces managed by MaterialGroups - they handle their own refresh
        // MaterialGroupManager.checkZoomAndRefresh() will update these after cuboid updates
        if (this.faceGroups && this.faceGroups[face]) {
          return;
        }

        // Handle video materials - update border and offset without recreating video element
        if (material.type === 'video') {
          // Update border with correct zoom (preserve video playback)
          const scaledBorderWidth = material.borderWidth * effectiveZoom;
          faceEl.style.border = `${scaledBorderWidth}px ${material.borderStyle || 'solid'} ${material.borderColor}`;
          // Update video offset for fragments (positions video within fragment bounds)
          this.applyVideoOffset(faceEl, face, effectiveZoom);
          // Re-apply hidden edges (border removal at split boundaries)
          this.applyHiddenEdges(faceEl, face);
          return;
        }

        // Handle webpage materials specially - update zoom transform without regenerating iframe
        if (material.type === 'webpage') {
          // Update border with correct zoom
          const scaledBorderWidth = material.borderWidth * effectiveZoom;
          faceEl.style.border = `${scaledBorderWidth}px ${material.borderStyle || 'solid'} ${material.borderColor}`;
          const webpageContainer = faceEl.querySelector('.webpage-container');
          if (webpageContainer) {
            // On Android, zoom is baked into face dimensions but iframe content needs scaling
            // Scale the container down, then transform scale up = iframe content appears zoomed
            if (effectiveZoom !== 1) {
              webpageContainer.style.transformOrigin = 'left top';
              webpageContainer.style.transform = `scale(${effectiveZoom})`;
              // Container is smaller but scaled up to fill face
              webpageContainer.style.width = `${100 / effectiveZoom}%`;
              webpageContainer.style.height = `${100 / effectiveZoom}%`;
            } else {
              webpageContainer.style.transform = '';
              webpageContainer.style.width = '100%';
              webpageContainer.style.height = '100%';
            }
          }
          return;
        }

        // Preserve selection classes
        const selectionClasses = [];
        if (faceEl.classList.contains('primary-selected')) selectionClasses.push('primary-selected');
        if (faceEl.classList.contains('multi-selected')) selectionClasses.push('multi-selected');
        if (faceEl.classList.contains('group-highlighted')) selectionClasses.push('group-highlighted');

        // Preserve face dimensions before clearing styles (needed for pixel-based gradients)
        const faceWidth = faceEl.style.width;
        const faceHeight = faceEl.style.height;

        // Preserve overlays before clearing innerHTML
        const aoOverlay = faceEl.querySelector('.ao-overlay');
        const shadowOverlay = faceEl.querySelector('.character-shadow-overlay');
        const shadingOverlay = faceEl.querySelector('.shading-overlay');

        faceEl.style.cssText = '';
        faceEl.innerHTML = '';
        faceEl.className = `face ${face} ${selectionClasses.join(' ')}`;

        // Restore preserved overlays
        if (aoOverlay) faceEl.appendChild(aoOverlay);
        if (shadowOverlay) faceEl.appendChild(shadowOverlay);
        if (shadingOverlay) faceEl.appendChild(shadingOverlay);

        // Restore face dimensions so material.apply can read them
        if (faceWidth) faceEl.style.width = faceWidth;
        if (faceHeight) faceEl.style.height = faceHeight;

        if (['right', 'left', 'front', 'back'].includes(face)) {
          faceEl.style.transformOrigin = 'left top';
        }
        material.apply(faceEl, effectiveZoom);
        // Apply texture offset for seamless fragment tiling
        this.applyTextureOffset(faceEl, face, effectiveZoom);
        // Remove borders at split boundaries
        this.applyHiddenEdges(faceEl, face);
        // Apply video offset for seamless video display
        this.applyVideoOffset(faceEl, face, effectiveZoom);
        // Apply gradient offset for seamless gradient display
        this.applyGradientOffset(faceEl, face, effectiveZoom);
        // Apply text offset for seamless text display
        this.applyTextOffset(faceEl, face, effectiveZoom);
        // Apply special effect offset for seamless metallic/holographic display
        this.applySpecialEffectOffset(faceEl, face, effectiveZoom);
      }
    });
  }

  /**
   * Apply world-space texture coordinates for seamless tiling.
   * Uses absolute world coordinates (like triplanar texturing) so that
   * when cuboids are subdivided, fragments maintain correct texture alignment.
   * @param {HTMLElement} faceEl - The face element
   * @param {string} face - The face name ('top', 'front', etc.)
   * @param {number} zoom - Current zoom level
   */
  applyTextureOffset(faceEl, face, zoom = 1) {
    const material = this.materials[face];
    if (!material || (material.type !== 'texture' && material.type !== 'pattern')) return;

    const cubeSize = this.cubeSize * zoom;

    // Calculate absolute world position (in pixels)
    // This matches how MaterialGroupManager.calculateWorldUV works
    const worldX = this.x * cubeSize;
    const worldY = this.y * cubeSize;
    const worldZ = this.z * cubeSize;

    // Cuboid dimensions (with zoom)
    const w = this.width * cubeSize;
    const h = this.height * cubeSize;
    const d = this.depth * cubeSize;

    // Get material's own offset settings (scaled by zoom)
    const matOffsetX = (material.offsetX || 0) * zoom;
    const matOffsetY = (material.offsetY || 0) * zoom;

    // Calculate UV coordinates for single-face texturing
    // Two modes:
    // 1. FRAGMENTS: Use origin-relative offsets so subdivisions align with original texture
    // 2. NON-FRAGMENTS: Use pure world coordinates for world-stationary texturing during resize
    // For mirrored faces (back, bottom), calculate from the far edge due to CSS transforms

    // Use textureOrigin for fragment offset calculations
    const originX = this.textureOrigin.x * cubeSize;
    const originY = this.textureOrigin.y * cubeSize;
    const originZ = this.textureOrigin.z * cubeSize;
    const originW = this.textureOrigin.width * cubeSize;
    const originH = this.textureOrigin.height * cubeSize;
    const originD = this.textureOrigin.depth * cubeSize;

    let u, v;

    // UNIFIED FORMULAS for both fragments and non-fragments
    // These formulas correctly handle CSS transform mirroring for each face
    // For fragments: textureOrigin = original parent bounds
    // For non-fragments: textureOrigin = preserved during resize, fresh on move/create
    // Result: textures stay world-stationary during resize, attached during move
    switch (face) {
      case 'top':
        // XZ plane projection - looking down
        // No mirroring needed
        u = (worldX - originX) + matOffsetX;
        v = (originZ + originD - worldZ - d) + matOffsetY;
        break;
      case 'bottom':
        // XZ plane projection - CSS element is flipped (scaleY(-1))
        // X is mirrored due to flip
        u = (originX + originW - worldX - w) + matOffsetX;
        v = (worldZ - originZ) + matOffsetY;
        break;
      case 'front':
        // XY plane projection - looking at front
        // No X mirroring, Y inverted for CSS
        u = (worldX - originX) + matOffsetX;
        v = (originY + originH - worldY - h) + matOffsetY;
        break;
      case 'back':
        // XY plane projection - CSS element is rotated 180° around Y
        // X is mirrored due to rotation
        u = (originX + originW - worldX - w) + matOffsetX;
        v = (originY + originH - worldY - h) + matOffsetY;
        break;
      case 'left':
        // ZY plane projection - looking at left side
        // Z is inverted (higher Z = lower U in CSS)
        u = (originZ + originD - worldZ - d) + matOffsetX;
        v = (originY + originH - worldY - h) + matOffsetY;
        break;
      case 'right':
        // ZY plane projection - CSS element faces opposite direction
        // Z is NOT inverted
        u = (worldZ - originZ) + matOffsetX;
        v = (originY + originH - worldY - h) + matOffsetY;
        break;
      default:
        return;
    }

    // Calculate texture size based on material scale
    const scaleX = material.scaleX || 1;
    const scaleY = material.scaleY || 1;

    // originW, originH, originD already declared above for UV calculation

    // For textures, use ORIGINAL face dimensions as base tile size
    // For patterns, use pixel-based sizing
    let texWidth, texHeight;
    let isHorizontalStripes = false;
    let isVerticalStripes = false;

    if (material.type === 'texture') {
      switch (face) {
        case 'top':
        case 'bottom':
          texWidth = originW / scaleX;
          texHeight = originD / scaleY;
          break;
        case 'front':
        case 'back':
          texWidth = originW / scaleX;
          texHeight = originH / scaleY;
          break;
        case 'left':
        case 'right':
          texWidth = originD / scaleX;
          texHeight = originH / scaleY;
          break;
      }
      faceEl.style.backgroundSize = `${texWidth}px ${texHeight}px`;
    } else {
      // Pattern - parse existing background-size for wrapping calculation
      // Stripes use formats like "100% 20px" (horizontal) or "20px 100%" (vertical)
      const bgSize = faceEl.style.backgroundSize;

      // Try to parse "Npx Mpx" format first
      const pxPxMatch = bgSize.match(/^(\d+(?:\.\d+)?)px\s+(\d+(?:\.\d+)?)px$/);
      if (pxPxMatch) {
        texWidth = parseFloat(pxPxMatch[1]);
        texHeight = parseFloat(pxPxMatch[2]);
      } else {
        // Try "100% Npx" format (horizontal stripes)
        const percentPxMatch = bgSize.match(/^100%\s+(\d+(?:\.\d+)?)px$/);
        if (percentPxMatch) {
          isHorizontalStripes = true;
          texHeight = parseFloat(percentPxMatch[1]);
          texWidth = texHeight; // Use same period for both dimensions
        } else {
          // Try "Npx 100%" format (vertical stripes)
          const pxPercentMatch = bgSize.match(/^(\d+(?:\.\d+)?)px\s+100%$/);
          if (pxPercentMatch) {
            isVerticalStripes = true;
            texWidth = parseFloat(pxPercentMatch[1]);
            texHeight = texWidth; // Use same period for both dimensions
          } else {
            // Fallback for other patterns
            texWidth = 20 * zoom;
            texHeight = 20 * zoom;
          }
        }
      }
    }

    // Wrap UV coordinates within texture bounds for proper tiling
    let bgPosX, bgPosY;
    if (isHorizontalStripes) {
      // Horizontal stripes - only Y position matters, X is full width
      bgPosX = 0;
      bgPosY = -((v % texHeight + texHeight) % texHeight);
    } else if (isVerticalStripes) {
      // Vertical stripes - only X position matters, Y is full height
      bgPosX = -((u % texWidth + texWidth) % texWidth);
      bgPosY = 0;
    } else {
      // Regular pattern/texture - both dimensions tile
      bgPosX = -((u % texWidth + texWidth) % texWidth);
      bgPosY = -((v % texHeight + texHeight) % texHeight);
    }

    // Adjust for hidden border edges (for fragments at split boundaries)
    const borderWidth = (material.borderWidth || 0) * zoom;
    const hiddenEdges = this.hiddenEdges[face] || [];
    let adjustX = 0, adjustY = 0;
    if (hiddenEdges.includes('left')) {
      adjustX = borderWidth;
    }
    if (hiddenEdges.includes('top')) {
      adjustY = borderWidth;
    }

    faceEl.style.backgroundPosition = `${bgPosX + adjustX}px ${bgPosY + adjustY}px`;
  }

  /**
   * Apply hidden edges to a face element (remove borders at split boundaries).
   * @param {HTMLElement} faceEl - The face element
   * @param {string} face - The face name ('top', 'front', etc.)
   */
  applyHiddenEdges(faceEl, face) {
    const edges = this.hiddenEdges[face];
    if (!edges || edges.length === 0) return;

    // Remove borders on hidden edges
    for (const edge of edges) {
      switch (edge) {
        case 'top':
          faceEl.style.borderTop = 'none';
          break;
        case 'bottom':
          faceEl.style.borderBottom = 'none';
          break;
        case 'left':
          faceEl.style.borderLeft = 'none';
          break;
        case 'right':
          faceEl.style.borderRight = 'none';
          break;
      }
    }
  }

  /**
   * Apply video offset for seamless display across fragments.
   * For videos, we need to size the video to the original face dimensions
   * and position it to show only the portion visible in this fragment.
   * Uses a clipping container for proper overflow handling.
   * @param {HTMLElement} faceEl - The face element
   * @param {string} face - The face name ('top', 'front', etc.)
   * @param {number} zoom - Current zoom level
   */
  applyVideoOffset(faceEl, face, zoom = 1) {
    // Only apply for fragments with video material
    if (!this.isFragment) return;

    const material = this.materials[face];
    if (!material || material.type !== 'video') return;

    // Find or create video container for clipping
    // Use same class as MaterialGroupManager.applyVideoTriplanar for consistency
    let videoContainer = faceEl.querySelector('.video-triplanar-container');
    let video = faceEl.querySelector('video');
    if (!video) return;

    const cubeSize = this.cubeSize * zoom;

    // Calculate world position of this fragment (matching triplanar approach)
    const worldX = this.x * cubeSize;
    const worldY = this.y * cubeSize;
    const worldZ = this.z * cubeSize;

    // Original cuboid bounds (from textureOrigin)
    const boundsMinX = this.textureOrigin.x * cubeSize;
    const boundsMinZ = this.textureOrigin.z * cubeSize;
    const boundsMaxY = (this.textureOrigin.y + this.textureOrigin.height) * cubeSize;
    const boundsMaxZ = (this.textureOrigin.z + this.textureOrigin.depth) * cubeSize;

    // Get original face dimensions and calculate video offset based on face type
    // Handle each face according to its CSS transform orientation (matching applyTextureOffset):
    // - Bottom is flipped (scaleY(-1)), so X is mirrored
    // - Back is rotated 180° around Y, so X is mirrored
    // - Right faces opposite direction from left, so Z is NOT inverted for right
    let baseWidth, baseHeight, posU, posV;

    // Fragment dimensions
    const fragWidth = this.width * cubeSize;
    const fragHeight = this.height * cubeSize;
    const fragDepth = this.depth * cubeSize;

    // Bounds from texture origin
    const boundsMaxX = (this.textureOrigin.x + this.textureOrigin.width) * cubeSize;

    switch (face) {
      case 'top':
        // XZ plane projection - looking down
        // U: fragment's X offset from origin's left edge
        // V: fragment's Z offset from origin's front edge (inverted for CSS - higher Z = CSS top)
        baseWidth = this.textureOrigin.width * cubeSize;
        baseHeight = this.textureOrigin.depth * cubeSize;
        posU = worldX - boundsMinX;
        posV = boundsMaxZ - worldZ - fragDepth;
        break;
      case 'bottom':
        // XZ plane projection - CSS element is flipped (scaleY(-1))
        // U: offset from RIGHT edge (because X is mirrored after flip)
        // V: offset from BACK edge (Z NOT inverted due to flip)
        baseWidth = this.textureOrigin.width * cubeSize;
        baseHeight = this.textureOrigin.depth * cubeSize;
        posU = boundsMaxX - worldX - fragWidth;
        posV = worldZ - boundsMinZ;
        break;
      case 'front':
        // XY plane projection - looking at front
        // U: fragment's X offset from origin's left edge
        // V: fragment's Y offset from origin's top edge (inverted for CSS)
        baseWidth = this.textureOrigin.width * cubeSize;
        baseHeight = this.textureOrigin.height * cubeSize;
        posU = worldX - boundsMinX;
        posV = boundsMaxY - worldY - fragHeight;
        break;
      case 'back':
        // XY plane projection - CSS element is rotated 180° around Y
        // U: offset from RIGHT edge (because X is mirrored)
        // V: same as front (Y not affected by Y-rotation)
        baseWidth = this.textureOrigin.width * cubeSize;
        baseHeight = this.textureOrigin.height * cubeSize;
        posU = boundsMaxX - worldX - fragWidth;
        posV = boundsMaxY - worldY - fragHeight;
        break;
      case 'left':
        // ZY plane projection - looking at left side
        // U: offset from FRONT edge (inverted Z - higher Z = lower U)
        // V: fragment's Y offset from origin's top edge (inverted for CSS)
        baseWidth = this.textureOrigin.depth * cubeSize;
        baseHeight = this.textureOrigin.height * cubeSize;
        posU = boundsMaxZ - worldZ - fragDepth;
        posV = boundsMaxY - worldY - fragHeight;
        break;
      case 'right':
        // ZY plane projection - CSS element faces opposite direction
        // U: offset from BACK edge (Z NOT inverted for right face)
        // V: same as left
        baseWidth = this.textureOrigin.depth * cubeSize;
        baseHeight = this.textureOrigin.height * cubeSize;
        posU = worldZ - boundsMinZ;
        posV = boundsMaxY - worldY - fragHeight;
        break;
      default:
        return;
    }

    // Adjust for hidden border edges
    // When a border is hidden at a split boundary, the content area shifts
    const borderWidth = (material.borderWidth || 0) * zoom;
    const hiddenEdges = this.hiddenEdges[face] || [];
    if (hiddenEdges.includes('left')) {
      posU -= borderWidth;
    }
    if (hiddenEdges.includes('top')) {
      posV -= borderWidth;
    }

    // Create container for clipping if it doesn't exist
    // This ensures the oversized video is properly clipped to the fragment bounds
    // Use same class as MaterialGroupManager.applyVideoTriplanar for consistency
    if (!videoContainer) {
      videoContainer = document.createElement('div');
      videoContainer.className = 'video-triplanar-container';
      videoContainer.style.position = 'absolute';
      videoContainer.style.top = '0';
      videoContainer.style.left = '0';
      videoContainer.style.width = '100%';
      videoContainer.style.height = '100%';
      videoContainer.style.overflow = 'hidden';
      videoContainer.style.pointerEvents = 'none';

      // Move video into container
      faceEl.appendChild(videoContainer);
      videoContainer.appendChild(video);
    }

    // Size and position the video to match triplanar approach
    video.style.position = 'absolute';
    video.style.width = `${baseWidth}px`;
    video.style.height = `${baseHeight}px`;
    video.style.left = `${-posU}px`;
    video.style.top = `${-posV}px`;
    video.style.transformOrigin = '0 0';
    video.style.objectFit = 'cover';
    video.style.pointerEvents = 'none';

    // Sync video playback to parent's video for seamless subdivision
    this.syncVideoToParent(video, face);
  }

  /**
   * Sync a fragment's video playback time to the parent cuboid's video.
   * Prefers the material's persistent audio source (most reliable time reference),
   * falls back to the parent cuboid's face video element.
   * @param {HTMLVideoElement} fragmentVideo - The fragment's video element
   * @param {string} face - The face name
   */
  syncVideoToParent(fragmentVideo, face) {
    if (!this.parentKey) return;

    // Prefer the material's audio source as time reference (never disrupted by k-d tree)
    const material = this.materials[face];
    if (material && material._audioSource && material._audioSource.currentTime) {
      const timeDiff = Math.abs(fragmentVideo.currentTime - material._audioSource.currentTime);
      if (timeDiff > 0.1) {
        fragmentVideo.currentTime = material._audioSource.currentTime;
      }
      return;
    }

    // Fallback: sync from parent cuboid's face video
    const world = window.game?.world;
    if (!world) return;

    const parentCuboid = world.cuboids.get(this.parentKey);
    if (!parentCuboid) return;

    const parentFaceEl = parentCuboid.faceElements?.[face];
    if (!parentFaceEl) return;

    const parentVideo = parentFaceEl.querySelector('video');
    if (!parentVideo || !parentVideo.currentTime) return;

    const timeDiff = Math.abs(fragmentVideo.currentTime - parentVideo.currentTime);
    if (timeDiff > 0.1) {
      fragmentVideo.currentTime = parentVideo.currentTime;
    }
  }

  /**
   * Resume any paused videos on this cuboid.
   * Called when a cuboid becomes visible again after frustum culling,
   * since browsers may pause videos when elements are hidden (display: none).
   */
  resumeVideos() {
    if (!this.faceElements) return;

    for (const face of Object.keys(this.faceElements)) {
      const faceEl = this.faceElements[face];
      if (!faceEl) continue;

      const video = faceEl.querySelector('video');
      if (video && video.paused) {
        video.play().catch(() => {});
      }
    }
  }

  /**
   * Apply gradient offset for seamless display across fragments.
   * Uses background-size/position approach to properly handle material scale and offset.
   * @param {HTMLElement} faceEl - The face element
   * @param {string} face - The face name ('top', 'front', etc.)
   * @param {number} zoom - Current zoom level
   */
  applyGradientOffset(faceEl, face, zoom = 1) {
    // Only apply for fragments with gradient material
    if (!this.isFragment) return;

    const material = this.materials[face];
    if (!material || material.type !== 'gradient' || !material.gradient) return;

    const { type, colors, direction } = material.gradient;
    const cubeSize = this.cubeSize * zoom;

    // Get material's scale and offset (default to 1 and 0)
    const matScaleX = material.scaleX || 1;
    const matScaleY = material.scaleY || 1;
    const matOffsetX = (material.offsetX || 0) * zoom;
    const matOffsetY = (material.offsetY || 0) * zoom;

    // Calculate fragment offset from original origin (in pixels)
    const fragOffsetX = (this.x - this.textureOrigin.x) * cubeSize;
    const fragOffsetY = (this.y - this.textureOrigin.y) * cubeSize;
    const fragOffsetZ = (this.z - this.textureOrigin.z) * cubeSize;

    // Get original face dimensions
    let originalWidth, originalHeight;
    let gradientOffsetX = 0, gradientOffsetY = 0;

    switch (face) {
      case 'top':
      case 'bottom':
        originalWidth = this.textureOrigin.width * cubeSize;
        originalHeight = this.textureOrigin.depth * cubeSize;
        gradientOffsetX = fragOffsetX;
        gradientOffsetY = fragOffsetZ;
        break;
      case 'front':
      case 'back':
        originalWidth = this.textureOrigin.width * cubeSize;
        originalHeight = this.textureOrigin.height * cubeSize;
        gradientOffsetX = fragOffsetX;
        gradientOffsetY = fragOffsetY;
        break;
      case 'left':
      case 'right':
        originalWidth = this.textureOrigin.depth * cubeSize;
        originalHeight = this.textureOrigin.height * cubeSize;
        gradientOffsetX = fragOffsetZ;
        gradientOffsetY = fragOffsetY;
        break;
      default:
        return;
    }

    // Adjust for hidden border edges
    // When a border is hidden at a split boundary, the content area shifts
    const borderWidth = (material.borderWidth || 0) * zoom;
    const hiddenEdges = this.hiddenEdges[face] || [];
    if (hiddenEdges.includes('left')) {
      gradientOffsetX -= borderWidth;
    }
    if (hiddenEdges.includes('top')) {
      gradientOffsetY -= borderWidth;
    }

    if (type === 'linear') {
      // For linear gradients, use background-size/position to tile the gradient
      // Scale affects the size of each gradient tile
      const bgWidth = originalWidth * matScaleX;
      const bgHeight = originalHeight * matScaleY;

      // Position accounts for both fragment offset and material offset
      const bgPosX = -gradientOffsetX + matOffsetX;
      const bgPosY = -gradientOffsetY + matOffsetY;

      faceEl.style.backgroundSize = `${bgWidth}px ${bgHeight}px`;
      faceEl.style.backgroundPosition = `${bgPosX}px ${bgPosY}px`;
      faceEl.style.backgroundRepeat = 'repeat';
    } else if (type === 'radial') {
      // For radial gradients, use pixel-based approach (same as triplanar)
      // Center position adjusted for fragment offset, color stops in pixels
      const shape = direction || 'circle';
      const avgScale = (matScaleX + matScaleY) / 2;

      // Calculate where the gradient center should appear in this fragment
      // Original center is at 50% of original dimensions, plus material offset
      const originalCenterX = originalWidth / 2 + matOffsetX;
      const originalCenterY = originalHeight / 2 + matOffsetY;

      // Fragment's position within original
      const centerInFragX = originalCenterX - gradientOffsetX;
      const centerInFragY = originalCenterY - gradientOffsetY;

      // Use original face dimensions so gradient stays "attached" to the face
      // and scales proportionally when zooming (matching single-face behavior)
      const halfW = originalWidth / 2;
      const halfH = originalHeight / 2;
      const maxRadius = Math.sqrt(halfW * halfW + halfH * halfH) / avgScale;

      // Build pixel-based color stops (same approach as triplanar)
      const stops = colors.map((color, i) => {
        const t = i / (colors.length - 1); // 0 to 1 position
        const radius = t * maxRadius;
        return `${color} ${radius}px`;
      });

      // Extend last color to ensure full coverage at all zoom levels
      stops.push(`${colors[colors.length - 1]} ${maxRadius * 3}px`);

      faceEl.style.backgroundImage = `radial-gradient(${shape} at ${centerInFragX}px ${centerInFragY}px, ${stops.join(', ')})`;
    }
  }

  /**
   * Interpolate gradient colors for a subsection of the gradient.
   * @param {string[]} colors - Original color stops (may include percentages)
   * @param {number} start - Start position (0-1)
   * @param {number} end - End position (0-1)
   * @return {string[]} - Adjusted color stops
   */
  interpolateGradientColors(colors, start, end) {
    if (!colors || colors.length < 2) return colors || ['#000', '#fff'];

    const range = end - start;
    if (range <= 0) {
      // Entire fragment is a single color - interpolate
      return [this.getGradientColorAt(colors, start)];
    }

    // Parse colors and their positions
    const stops = colors.map((c, i) => {
      const match = c.match(/^(.+?)\s+(\d+)%$/);
      if (match) {
        return { color: match[1], pos: parseInt(match[2]) / 100 };
      }
      return { color: c, pos: i / (colors.length - 1) };
    });

    // Find color stops that fall within or straddle the visible range
    const result = [];

    // Add starting color (interpolated if between stops)
    result.push(`${this.getGradientColorAt(colors, start)} 0%`);

    // Add intermediate stops that fall within range
    for (const stop of stops) {
      if (stop.pos > start && stop.pos < end) {
        const adjustedPos = ((stop.pos - start) / range) * 100;
        result.push(`${stop.color} ${adjustedPos.toFixed(1)}%`);
      }
    }

    // Add ending color
    result.push(`${this.getGradientColorAt(colors, end)} 100%`);

    return result;
  }

  /**
   * Get interpolated color at a specific position in the gradient.
   * @param {string[]} colors - Color stops
   * @param {number} pos - Position (0-1)
   * @return {string} - Interpolated color
   */
  getGradientColorAt(colors, pos) {
    if (!colors || colors.length === 0) return '#000';
    if (colors.length === 1) return colors[0];

    // Clamp position
    pos = Math.max(0, Math.min(1, pos));

    // Parse color stops
    const stops = colors.map((c, i) => {
      const match = c.match(/^(.+?)\s+(\d+)%$/);
      if (match) {
        return { color: match[1].trim(), pos: parseInt(match[2]) / 100 };
      }
      return { color: c.trim(), pos: i / (colors.length - 1) };
    });

    // Find surrounding stops
    let before = stops[0];
    let after = stops[stops.length - 1];

    for (let i = 0; i < stops.length; i++) {
      if (stops[i].pos <= pos) before = stops[i];
      if (stops[i].pos >= pos && stops[i].pos < after.pos) after = stops[i];
    }

    if (before.pos >= pos) return before.color;
    if (after.pos <= pos) return after.color;

    // Interpolate between before and after
    const t = (pos - before.pos) / (after.pos - before.pos);
    return this.lerpColor(before.color, after.color, t);
  }

  /**
   * Linear interpolation between two colors.
   * @param {string} color1 - First color (hex or rgb)
   * @param {string} color2 - Second color (hex or rgb)
   * @param {number} t - Interpolation factor (0-1)
   * @return {string} - Interpolated color as rgb()
   */
  lerpColor(color1, color2, t) {
    const c1 = this.parseColor(color1);
    const c2 = this.parseColor(color2);

    const r = Math.round(c1.r + (c2.r - c1.r) * t);
    const g = Math.round(c1.g + (c2.g - c1.g) * t);
    const b = Math.round(c1.b + (c2.b - c1.b) * t);

    return `rgb(${r}, ${g}, ${b})`;
  }

  /**
   * Parse a color string to RGB components.
   * @param {string} color - Color string (hex or rgb)
   * @return {Object} - {r, g, b} values
   */
  parseColor(color) {
    // Handle hex colors
    if (color.startsWith('#')) {
      const hex = color.slice(1);
      if (hex.length === 3) {
        return {
          r: parseInt(hex[0] + hex[0], 16),
          g: parseInt(hex[1] + hex[1], 16),
          b: parseInt(hex[2] + hex[2], 16)
        };
      }
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16)
      };
    }

    // Handle rgb() colors
    const match = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (match) {
      return { r: parseInt(match[1]), g: parseInt(match[2]), b: parseInt(match[3]) };
    }

    // Fallback
    return { r: 0, g: 0, b: 0 };
  }

  /**
   * Apply text offset for fragment text to create seamless text display.
   * Similar to applyVideoOffset/applyGradientOffset - positions text relative to original cuboid.
   * @param {HTMLElement} faceEl - The face element
   * @param {string} face - The face name (front, back, left, right, top, bottom)
   * @param {number} zoom - Current zoom level
   */
  applyTextOffset(faceEl, face, zoom = 1) {
    // Only apply for fragments
    if (!this.isFragment) return;

    const material = this.materials[face];
    if (!material || !material.text) return;

    // Get or create the text container
    let textContainer = faceEl.querySelector('.face-text');
    if (!textContainer) return; // Text hasn't been applied yet

    const cubeSize = this.cubeSize * zoom;

    // Calculate world positions and dimensions
    const boundsMinX = this.textureOrigin.x * cubeSize;
    const boundsMinZ = this.textureOrigin.z * cubeSize;
    const boundsMaxY = (this.textureOrigin.y + this.textureOrigin.height) * cubeSize;
    const boundsMaxZ = (this.textureOrigin.z + this.textureOrigin.depth) * cubeSize;

    const worldX = this.x * cubeSize;
    const worldY = this.y * cubeSize;
    const worldZ = this.z * cubeSize;

    // Get original and fragment dimensions for this face
    // Handle each face according to its CSS transform orientation (matching applyTextureOffset):
    // - Bottom is flipped (scaleY(-1)), so X is mirrored
    // - Back is rotated 180° around Y, so X is mirrored
    // - Right faces opposite direction from left, so Z is NOT inverted for right
    let originalWidth, originalHeight, posU, posV;

    // Fragment dimensions
    const fragWidth = this.width * cubeSize;
    const fragHeight = this.height * cubeSize;
    const fragDepth = this.depth * cubeSize;

    // Bounds from texture origin
    const boundsMaxX = (this.textureOrigin.x + this.textureOrigin.width) * cubeSize;

    switch (face) {
      case 'top':
        // XZ plane projection - looking down
        // U: fragment's X offset from origin's left edge
        // V: fragment's Z offset from origin's front edge (inverted for CSS - higher Z = CSS top)
        originalWidth = this.textureOrigin.width * cubeSize;
        originalHeight = this.textureOrigin.depth * cubeSize;
        posU = worldX - boundsMinX;
        posV = boundsMaxZ - worldZ - fragDepth;
        break;
      case 'bottom':
        // XZ plane projection - CSS element is flipped (scaleY(-1))
        // U: offset from RIGHT edge (because X is mirrored after flip)
        // V: offset from BACK edge (Z NOT inverted due to flip)
        originalWidth = this.textureOrigin.width * cubeSize;
        originalHeight = this.textureOrigin.depth * cubeSize;
        posU = boundsMaxX - worldX - fragWidth;
        posV = worldZ - boundsMinZ;
        break;
      case 'front':
        // XY plane projection - looking at front
        // U: fragment's X offset from origin's left edge
        // V: fragment's Y offset from origin's top edge (inverted for CSS)
        originalWidth = this.textureOrigin.width * cubeSize;
        originalHeight = this.textureOrigin.height * cubeSize;
        posU = worldX - boundsMinX;
        posV = boundsMaxY - worldY - fragHeight;
        break;
      case 'back':
        // XY plane projection - CSS element is rotated 180° around Y
        // U: offset from RIGHT edge (because X is mirrored)
        // V: same as front (Y not affected by Y-rotation)
        originalWidth = this.textureOrigin.width * cubeSize;
        originalHeight = this.textureOrigin.height * cubeSize;
        posU = boundsMaxX - worldX - fragWidth;
        posV = boundsMaxY - worldY - fragHeight;
        break;
      case 'left':
        // ZY plane projection - looking at left side
        // U: offset from FRONT edge (inverted Z - higher Z = lower U)
        // V: fragment's Y offset from origin's top edge (inverted for CSS)
        originalWidth = this.textureOrigin.depth * cubeSize;
        originalHeight = this.textureOrigin.height * cubeSize;
        posU = boundsMaxZ - worldZ - fragDepth;
        posV = boundsMaxY - worldY - fragHeight;
        break;
      case 'right':
        // ZY plane projection - CSS element faces opposite direction
        // U: offset from BACK edge (Z NOT inverted for right face)
        // V: same as left
        originalWidth = this.textureOrigin.depth * cubeSize;
        originalHeight = this.textureOrigin.height * cubeSize;
        posU = worldZ - boundsMinZ;
        posV = boundsMaxY - worldY - fragHeight;
        break;
      default:
        return;
    }

    // Adjust for hidden border edges
    // When a border is hidden at a split boundary, the content area shifts
    const borderWidth = (material.borderWidth || 0) * zoom;
    const hiddenEdges = this.hiddenEdges[face] || [];
    if (hiddenEdges.includes('left')) {
      posU -= borderWidth;
    }
    if (hiddenEdges.includes('top')) {
      posV -= borderWidth;
    }

    // Get material scale and offset settings
    const scaleX = material.scaleX || 1;
    const scaleY = material.scaleY || 1;
    const textScale = (scaleX + scaleY) / 2;
    const matOffsetX = (material.offsetX || 0) * zoom;
    const matOffsetY = (material.offsetY || 0) * zoom;

    // Parse base font size and apply scaling
    const baseFontSize = parseFloat(material.textSize) || 12;
    const fontUnit = (material.textSize || '12px').replace(/[\d.]/g, '') || 'px';
    const scaledFontSize = baseFontSize * zoom * textScale;

    // For fragments, we need to create a text element that spans the entire original cuboid
    // and position it so only the correct portion is visible in this fragment
    // Clear existing text container and recreate with proper positioning
    textContainer.style.position = 'absolute';
    textContainer.style.top = '0';
    textContainer.style.left = '0';
    textContainer.style.width = '100%';
    textContainer.style.height = '100%';
    textContainer.style.overflow = 'hidden';
    textContainer.style.transform = ''; // Clear any transform set by Material.applyText()
    textContainer.style.display = 'block'; // Override flexbox from Material.applyText()

    // Get or create inner text element that spans the full original size
    let innerText = textContainer.querySelector('.fragment-text-inner');
    if (!innerText) {
      innerText = document.createElement('div');
      innerText.className = 'fragment-text-inner';
      textContainer.innerHTML = '';
      textContainer.appendChild(innerText);
    }

    // Size the inner element to the original cuboid's face dimensions
    innerText.style.position = 'absolute';
    innerText.style.width = `${originalWidth}px`;
    innerText.style.height = `${originalHeight}px`;
    innerText.style.left = `${-posU + matOffsetX}px`;
    innerText.style.top = `${-posV + matOffsetY}px`;

    // Text styling - centered within the original bounds
    innerText.style.display = 'flex';
    innerText.style.justifyContent = 'center';
    innerText.style.alignItems = 'center';
    innerText.style.color = material.textColor || '#ffffff';
    innerText.style.fontSize = `${scaledFontSize}${fontUnit}`;
    innerText.style.fontFamily = material.textFont || 'Arial, sans-serif';
    innerText.style.textAlign = 'center';
    innerText.style.whiteSpace = 'nowrap';
    innerText.style.pointerEvents = 'none';

    innerText.textContent = material.text;
  }

  /**
   * Apply special effect offset for fragment metallic/holographic to create seamless display.
   * Similar to applyTextureOffset - uses UV-based positioning for seamless subdivision.
   * @param {HTMLElement} faceEl - The face element
   * @param {string} face - The face name (front, back, left, right, top, bottom)
   * @param {number} zoom - Current zoom level
   */
  applySpecialEffectOffset(faceEl, face, zoom = 1) {
    const material = this.materials[face];
    if (!material || material.type !== 'special') return;

    const effectType = material.effectType;
    // Only metallic and holographic have background patterns that need UV alignment
    if (effectType !== 'metallic' && effectType !== 'holographic') return;

    const cubeSize = this.cubeSize * zoom;

    // Calculate absolute world position (in pixels)
    const worldX = this.x * cubeSize;
    const worldY = this.y * cubeSize;
    const worldZ = this.z * cubeSize;

    // Cuboid dimensions (with zoom)
    const h = this.height * cubeSize;
    const d = this.depth * cubeSize;

    // Calculate UV coordinates based on face orientation (triplanar projection)
    // This matches MaterialGroupManager.calculateWorldUV
    let u, v;
    switch (face) {
      case 'top':
        u = worldX;
        v = -(worldZ + d);
        break;
      case 'bottom':
        u = -worldX;
        v = (worldZ + d);
        break;
      case 'front':
        u = worldX;
        v = -(worldY + h);
        break;
      case 'back':
        u = -worldX;
        v = -(worldY + h);
        break;
      case 'left':
        u = worldZ;
        v = -(worldY + h);
        break;
      case 'right':
        u = -worldZ;
        v = -(worldY + h);
        break;
      default:
        return;
    }

    // Calculate effect period based on type
    let period;
    let direction;
    if (effectType === 'metallic') {
      period = 40 * zoom; // Matches Material.applySpecialEffect (mp * 4 where mp = 10 * zoom)
      direction = material.metallicDirection || '0deg';
    } else {
      period = 70 * zoom; // Matches Material.applySpecialEffect (hp * 7)
      direction = material.holographicDirection || '0deg';
    }

    // Set position based on direction (like stripes)
    let posX, posY;
    if (direction === '0deg' || direction === '180deg') {
      // Horizontal effect - tile vertically, only Y position matters
      faceEl.style.backgroundSize = `100% ${period}px`;
      posX = 0;
      posY = -((v % period + period) % period);
    } else {
      // Vertical effect - tile horizontally, only X position matters
      faceEl.style.backgroundSize = `${period}px 100%`;
      posX = -((u % period + period) % period);
      posY = 0;
    }

    faceEl.style.backgroundPosition = `${posX}px ${posY}px`;
  }

  /**
   * Apply ambient occlusion texture to a face.
   * Uses a pseudo-element-like overlay that works with all background types.
   * @param {string} face - The face name ('top', 'front', etc.)
   * @param {string} textureURL - Data URL of the AO texture
   */
  applyAOTexture(face, textureURL) {
    const faceEl = this.faceElements[face];
    if (!faceEl || !textureURL) return;

    // Track AO overlays per face
    if (!this._aoOverlays) this._aoOverlays = {};

    let overlay = this._aoOverlays[face];

    // Check if overlay was removed from DOM
    if (overlay && !faceEl.contains(overlay)) {
      overlay = null;
    }

    // Skip if overlay exists with same texture (optimization)
    if (overlay && overlay._textureURL === textureURL) {
      overlay.style.display = 'block';
      return;
    }

    // Create overlay if needed
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'ao-overlay';
      faceEl.appendChild(overlay);
      this._aoOverlays[face] = overlay;
    }

    // Track current texture for change detection
    overlay._textureURL = textureURL;

    // Style the overlay to cover the full face
    // With border-box + overflow:hidden, the overlay (inset:0) fills the padding box (inside borders).
    // The AO texture was generated for the full face (including border area), so we size the
    // background to the full face dimensions and offset it to compensate for the border.
    // This keeps the AO correctly proportioned instead of being squished into the content area.
    const bt = parseFloat(faceEl.style.borderTopWidth) || 0;
    const br = parseFloat(faceEl.style.borderRightWidth) || 0;
    const bb = parseFloat(faceEl.style.borderBottomWidth) || 0;
    const bl = parseFloat(faceEl.style.borderLeftWidth) || 0;
    const hasBorder = bt || br || bb || bl;

    let bgSize, bgPosition;
    if (hasBorder) {
      // Content area = face dimensions minus borders (due to border-box)
      // Size the AO background to the full face (content + border) so proportions are correct
      const contentW = faceEl.clientWidth;
      const contentH = faceEl.clientHeight;
      const fullW = contentW + bl + br;
      const fullH = contentH + bt + bb;
      bgSize = `${fullW}px ${fullH}px`;
      bgPosition = `${-bl}px ${-bt}px`;
    } else {
      bgSize = '100% 100%';
      bgPosition = '0 0';
    }

    Object.assign(overlay.style, {
      position: 'absolute',
      inset: '0',
      pointerEvents: 'none',
      backgroundImage: `url(${textureURL})`,
      backgroundSize: bgSize,
      backgroundPosition: bgPosition,
      backgroundRepeat: 'no-repeat',
      mixBlendMode: 'multiply',
      zIndex: '1000',
      display: 'block',
    });
  }

  /**
   * Remove ambient occlusion texture from a face.
   * Hides the AO overlay.
   * @param {string} face - The face name ('top', 'front', etc.)
   */
  removeAOTexture(face) {
    if (this._aoOverlays && this._aoOverlays[face]) {
      this._aoOverlays[face].style.display = 'none';
    }
  }

  /**
   * Check if a face has AO texture applied.
   * @param {string} face - The face name
   * @returns {boolean} True if AO is applied and visible
   */
  hasAOTexture(face) {
    const overlay = this._aoOverlays && this._aoOverlays[face];
    return overlay && overlay.style.display !== 'none';
  }

  /**
   * Apply shading overlay to a face (Safari-compatible alternative to CSS brightness filter).
   * Uses semi-transparent black/white overlays instead of filter: brightness() which
   * causes rendering failures on Safari/iOS with large 3D-transformed elements.
   * @param {string} face - The face name ('top', 'front', etc.)
   * @param {number} brightness - Brightness value (1 = neutral, <1 = darker, >1 = lighter)
   */
  applyShadingOverlay(face, brightness) {
    const faceEl = this.faceElements[face];
    if (!faceEl) return;

    // Track shading overlays per face
    if (!this._shadingOverlays) this._shadingOverlays = {};

    let overlay = this._shadingOverlays[face];

    // Check if overlay was removed from DOM (e.g., by material refresh)
    if (overlay && !faceEl.contains(overlay)) {
      overlay = null;
    }

    // Create overlay if needed
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'shading-overlay';
      faceEl.appendChild(overlay);
      this._shadingOverlays[face] = overlay;
    }

    // Calculate overlay color and opacity based on brightness
    // brightness < 1: darken with black overlay
    // brightness > 1: lighten with white overlay
    // brightness = 1: fully transparent (no effect)
    let backgroundColor, opacity;

    if (brightness < 1) {
      // Darken: black overlay with opacity = 1 - brightness
      backgroundColor = '0, 0, 0';
      opacity = 1 - brightness;
    } else if (brightness > 1) {
      // Lighten: white overlay with opacity proportional to brightness increase
      // Cap at reasonable max to avoid blowing out colors
      backgroundColor = '255, 255, 255';
      opacity = Math.min(0.5, (brightness - 1) * 0.5);
    } else {
      // Neutral: no shading needed
      backgroundColor = '0, 0, 0';
      opacity = 0;
    }

    // Style the overlay
    Object.assign(overlay.style, {
      position: 'absolute',
      inset: '0',
      pointerEvents: 'none',
      backgroundColor: `rgba(${backgroundColor}, ${opacity})`,
      zIndex: '998', // Below AO (1000) and character shadows (999)
      display: opacity > 0.001 ? 'block' : 'none'
    });
  }

  /**
   * Compute a unique key for this cuboid.
   * @return {string} Unique key.
   */
  getKey() {
    return `${this.x},${this.y},${this.z},${this.width},${this.height},${this.depth}`;
  }

  /**
   * Get the options object that can recreate this cuboid.
   * Used for subdivision to create child fragments.
   * @return {Object} Options object.
   */
  getOptions() {
    // Deep copy hiddenEdges
    const hiddenEdgesCopy = {};
    for (const [face, edges] of Object.entries(this.hiddenEdges)) {
      hiddenEdgesCopy[face] = [...edges];
    }

    return {
      x: this.x,
      y: this.y,
      z: this.z,
      width: this.width,
      height: this.height,
      depth: this.depth,
      cubeSize: this.cubeSize,
      materials: { ...this.materials },
      hiddenFaces: [...this.hiddenFaces],
      hiddenEdges: hiddenEdgesCopy,
      isFragment: this.isFragment,
      parentKey: this.parentKey,
      // Preserve original texture origin and dimensions for seamless tiling
      textureOrigin: {
        x: this.textureOrigin.x,
        y: this.textureOrigin.y,
        z: this.textureOrigin.z,
        width: this.textureOrigin.width,
        height: this.textureOrigin.height,
        depth: this.textureOrigin.depth
      }
    };
  }

  /**
   * Check if this cuboid has any faces with materials that cannot be subdivided.
   * Materials with noSubdivide=true (like iframe/webpage materials) cannot be split across fragments.
   * @return {boolean} True if any face has a noSubdivide material
   */
  hasNoSubdivideMaterial() {
    for (const face of Object.keys(this.materials)) {
      const material = this.materials[face];
      if (material && material.noSubdivide) {
        return true;
      }
    }
    return false;
  }

  /**
   * Check if this cuboid has any webpage material (iframe).
   * Used for DOM-stable rendering to prevent iframe reload.
   * @return {boolean} True if any face has a webpage material
   */
  hasWebpageMaterial() {
    for (const face of Object.keys(this.materials)) {
      const material = this.materials[face];
      if (material && material.type === 'webpage') {
        return true;
      }
    }
    return false;
  }

  /**
   * Split this cuboid into two parts along an axis-aligned plane.
   * Used by k-d tree to resolve depth cycles.
   * @param {string} axis - 'x', 'y', or 'z'
   * @param {number} position - World coordinate to split at
   * @return {[Cuboid, Cuboid]|null} Two new cuboids, or null if split is outside bounds
   */
  subdivide(axis, position) {
    const min = this[axis];
    const sizeKey = axis === 'x' ? 'width' : axis === 'y' ? 'height' : 'depth';
    const size = this[sizeKey];
    const max = min + size;

    // Check if split is within bounds (with small epsilon for edge cases)
    const EPSILON = 0.0001;
    if (position <= min + EPSILON || position >= max - EPSILON) {
      return null;
    }

    const size1 = position - min;
    const size2 = max - position;

    // Determine which faces to hide at the split plane
    // Part1 is the "lower" part (smaller coordinate), Part2 is "higher"
    const hiddenFacesMap = {
      x: ['right', 'left'],   // Part1 hides right (at split), Part2 hides left
      y: ['top', 'bottom'],   // Part1 hides top (at split), Part2 hides bottom
      z: ['back', 'front']    // Part1 hides back (at split), Part2 hides front
    };

    // Determine which edges of VISIBLE faces should have no border (at split boundary)
    // Each part can have multiple {edge, faces} entries for different face groups
    const hiddenEdgesMap = {
      x: {
        // X-axis split: Part1 is lower X (left), Part2 is higher X (right)
        // The split boundary is at Part1's right edge and Part2's left edge
        part1: [{ edge: 'right', faces: ['top', 'bottom', 'front', 'back'] }],
        part2: [{ edge: 'left', faces: ['top', 'bottom', 'front', 'back'] }]
      },
      y: {
        // Y-axis split: Part1 is lower Y (bottom), Part2 is higher Y (top)
        // For side faces, CSS border-top = top of element = higher Y in world
        // Part1's split is at its top (higher Y end) → hide border-top
        // Part2's split is at its bottom (lower Y end) → hide border-bottom
        part1: [{ edge: 'top', faces: ['front', 'back', 'left', 'right'] }],
        part2: [{ edge: 'bottom', faces: ['front', 'back', 'left', 'right'] }]
      },
      z: {
        // Z-axis split: Part1 is lower Z (front), Part2 is higher Z (back)
        // Top/bottom faces: border-top = back (higher Z), border-bottom = front (lower Z)
        // Left/right faces: border-left = front (lower Z), border-right = back (higher Z)
        part1: [
          { edge: 'top', faces: ['top', 'bottom'] },     // back edge on horizontal faces
          { edge: 'right', faces: ['left', 'right'] }    // back edge on side faces
        ],
        part2: [
          { edge: 'bottom', faces: ['top', 'bottom'] },  // front edge on horizontal faces
          { edge: 'left', faces: ['left', 'right'] }     // front edge on side faces
        ]
      }
    };

    const part1Options = this.getOptions();
    const part2Options = this.getOptions();

    // Adjust dimensions based on split axis
    if (axis === 'x') {
      part1Options.width = size1;
      part2Options.x = position;
      part2Options.width = size2;
    } else if (axis === 'y') {
      part1Options.height = size1;
      part2Options.y = position;
      part2Options.height = size2;
    } else { // z
      part1Options.depth = size1;
      part2Options.z = position;
      part2Options.depth = size2;
    }

    // Set hidden faces at split boundary
    // Combine with any existing hidden faces from parent
    part1Options.hiddenFaces = [...new Set([...part1Options.hiddenFaces, hiddenFacesMap[axis][0]])];
    part2Options.hiddenFaces = [...new Set([...part2Options.hiddenFaces, hiddenFacesMap[axis][1]])];

    // Set hidden edges for visible faces at split boundary
    const edgeInfo = hiddenEdgesMap[axis];
    if (edgeInfo) {
      // Part1: hide specified edges on specified faces
      for (const { edge, faces } of edgeInfo.part1) {
        for (const face of faces) {
          if (!part1Options.hiddenFaces.includes(face)) {
            if (!part1Options.hiddenEdges[face]) part1Options.hiddenEdges[face] = [];
            if (!part1Options.hiddenEdges[face].includes(edge)) {
              part1Options.hiddenEdges[face].push(edge);
            }
          }
        }
      }
      // Part2: hide specified edges on specified faces
      for (const { edge, faces } of edgeInfo.part2) {
        for (const face of faces) {
          if (!part2Options.hiddenFaces.includes(face)) {
            if (!part2Options.hiddenEdges[face]) part2Options.hiddenEdges[face] = [];
            if (!part2Options.hiddenEdges[face].includes(edge)) {
              part2Options.hiddenEdges[face].push(edge);
            }
          }
        }
      }
    }

    // Mark as fragments with reference to original parent
    const originalParentKey = this.parentKey || this.getKey();
    part1Options.isFragment = true;
    part2Options.isFragment = true;
    part1Options.parentKey = originalParentKey;
    part2Options.parentKey = originalParentKey;

    // Propagate faceGroups so fragments know which faces are group-managed
    // This prevents refreshMaterials() from overwriting group textures with single-face textures
    if (this.faceGroups) {
      part1Options.faceGroups = { ...this.faceGroups };
      part2Options.faceGroups = { ...this.faceGroups };
    }

    // Create fragments - don't set currentZoom so update() will properly apply materials with zoom
    const part1 = new Cuboid(part1Options);
    const part2 = new Cuboid(part2Options);

    return [part1, part2];
  }

  /**
   * Subdivide this cuboid at MULTIPLE positions along an axis.
   * Much more efficient than calling subdivide() repeatedly - creates all fragments in one pass.
   *
   * @param {string} axis - The axis to split on ('x', 'y', or 'z')
   * @param {number[]} positions - Array of world coordinates to split at
   * @return {Cuboid[]|null} Array of fragments, or null if no valid splits
   */
  multiSubdivide(axis, positions) {
    const min = this[axis];
    const sizeKey = axis === 'x' ? 'width' : axis === 'y' ? 'height' : 'depth';
    const size = this[sizeKey];
    const max = min + size;

    const EPSILON = 0.0001;

    // Filter and sort valid positions (must be strictly inside bounds)
    const validPositions = [...new Set(positions)]
      .filter(p => p > min + EPSILON && p < max - EPSILON)
      .sort((a, b) => a - b);

    if (validPositions.length === 0) {
      return null;
    }

    // Hidden faces/edges maps (same as subdivide)
    const hiddenFacesMap = {
      x: ['right', 'left'],
      y: ['top', 'bottom'],
      z: ['back', 'front']
    };

    const hiddenEdgesMap = {
      x: {
        lower: [{ edge: 'right', faces: ['top', 'bottom', 'front', 'back'] }],
        upper: [{ edge: 'left', faces: ['top', 'bottom', 'front', 'back'] }]
      },
      y: {
        lower: [{ edge: 'top', faces: ['front', 'back', 'left', 'right'] }],
        upper: [{ edge: 'bottom', faces: ['front', 'back', 'left', 'right'] }]
      },
      z: {
        lower: [
          { edge: 'top', faces: ['top', 'bottom'] },
          { edge: 'right', faces: ['left', 'right'] }
        ],
        upper: [
          { edge: 'bottom', faces: ['top', 'bottom'] },
          { edge: 'left', faces: ['left', 'right'] }
        ]
      }
    };

    const originalParentKey = this.parentKey || this.getKey();
    const fragments = [];

    // Create N+1 fragments for N split positions
    // Boundaries: [min, pos0, pos1, ..., posN, max]
    const boundaries = [min, ...validPositions, max];

    for (let i = 0; i < boundaries.length - 1; i++) {
      const fragMin = boundaries[i];
      const fragMax = boundaries[i + 1];
      const fragSize = fragMax - fragMin;

      if (fragSize < EPSILON) continue; // Skip degenerate fragments

      const options = this.getOptions();

      // Set position and size for this axis
      if (axis === 'x') {
        options.x = fragMin;
        options.width = fragSize;
      } else if (axis === 'y') {
        options.y = fragMin;
        options.height = fragSize;
      } else {
        options.z = fragMin;
        options.depth = fragSize;
      }

      // Handle hidden faces and edges at internal boundaries
      const isFirst = (i === 0);
      const isLast = (i === boundaries.length - 2);
      const edgeInfo = hiddenEdgesMap[axis];

      // Hide upper face/edges (at fragMax boundary) if not last fragment
      if (!isLast) {
        options.hiddenFaces = [...new Set([...options.hiddenFaces, hiddenFacesMap[axis][0]])];
        for (const { edge, faces } of edgeInfo.lower) {
          for (const face of faces) {
            if (!options.hiddenFaces.includes(face)) {
              if (!options.hiddenEdges[face]) options.hiddenEdges[face] = [];
              if (!options.hiddenEdges[face].includes(edge)) {
                options.hiddenEdges[face].push(edge);
              }
            }
          }
        }
      }

      // Hide lower face/edges (at fragMin boundary) if not first fragment
      if (!isFirst) {
        options.hiddenFaces = [...new Set([...options.hiddenFaces, hiddenFacesMap[axis][1]])];
        for (const { edge, faces } of edgeInfo.upper) {
          for (const face of faces) {
            if (!options.hiddenFaces.includes(face)) {
              if (!options.hiddenEdges[face]) options.hiddenEdges[face] = [];
              if (!options.hiddenEdges[face].includes(edge)) {
                options.hiddenEdges[face].push(edge);
              }
            }
          }
        }
      }

      // Mark as fragment
      options.isFragment = true;
      options.parentKey = originalParentKey;

      // Propagate faceGroups so fragments know which faces are group-managed
      if (this.faceGroups) {
        options.faceGroups = { ...this.faceGroups };
      }

      fragments.push(new Cuboid(options));
    }

    return fragments.length > 0 ? fragments : null;
  }

  /**
   * Subdivide this cuboid along MULTIPLE axes simultaneously, creating a grid of fragments.
   * More efficient than sequential multiSubdivide calls - produces fewer fragments and
   * handles hidden faces/edges correctly at all internal boundaries in one pass.
   *
   * @param {number[]} xPositions - Split positions along X axis (world coordinates)
   * @param {number[]} zPositions - Split positions along Z axis (world coordinates)
   * @param {number[]} [yPositions=[]] - Split positions along Y axis (world coordinates)
   * @return {Cuboid[]|null} Array of fragments, or null if no valid splits
   */
  gridSubdivide(xPositions, zPositions, yPositions = []) {
    const EPSILON = 0.0001;

    // Filter and sort valid positions for each axis
    const filterPositions = (positions, min, max) => {
      return [...new Set(positions)]
        .filter(p => p > min + EPSILON && p < max - EPSILON)
        .sort((a, b) => a - b);
    };

    const validX = filterPositions(xPositions, this.x, this.x + this.width);
    const validY = filterPositions(yPositions, this.y, this.y + this.height);
    const validZ = filterPositions(zPositions, this.z, this.z + this.depth);

    // Need at least one valid split position across any axis
    if (validX.length === 0 && validY.length === 0 && validZ.length === 0) {
      return null;
    }

    // Build boundary arrays: [min, ...splits, max]
    const xBounds = [this.x, ...validX, this.x + this.width];
    const yBounds = [this.y, ...validY, this.y + this.height];
    const zBounds = [this.z, ...validZ, this.z + this.depth];

    const originalParentKey = this.parentKey || this.getKey();
    const fragments = [];

    for (let xi = 0; xi < xBounds.length - 1; xi++) {
      const fragX = xBounds[xi];
      const fragW = xBounds[xi + 1] - fragX;
      if (fragW < EPSILON) continue;

      const xFirst = (xi === 0);
      const xLast = (xi === xBounds.length - 2);

      for (let yi = 0; yi < yBounds.length - 1; yi++) {
        const fragY = yBounds[yi];
        const fragH = yBounds[yi + 1] - fragY;
        if (fragH < EPSILON) continue;

        const yFirst = (yi === 0);
        const yLast = (yi === yBounds.length - 2);

        for (let zi = 0; zi < zBounds.length - 1; zi++) {
          const fragZ = zBounds[zi];
          const fragD = zBounds[zi + 1] - fragZ;
          if (fragD < EPSILON) continue;

          const zFirst = (zi === 0);
          const zLast = (zi === zBounds.length - 2);

          const options = this.getOptions();
          options.x = fragX;
          options.width = fragW;
          options.y = fragY;
          options.height = fragH;
          options.z = fragZ;
          options.depth = fragD;

          // Hidden faces at internal boundaries
          // X-axis: right face hidden if not last X, left face hidden if not first X
          if (!xLast) {
            options.hiddenFaces = [...new Set([...options.hiddenFaces, 'right'])];
          }
          if (!xFirst) {
            options.hiddenFaces = [...new Set([...options.hiddenFaces, 'left'])];
          }
          // Y-axis: top face hidden if not last Y, bottom face hidden if not first Y
          if (!yLast) {
            options.hiddenFaces = [...new Set([...options.hiddenFaces, 'top'])];
          }
          if (!yFirst) {
            options.hiddenFaces = [...new Set([...options.hiddenFaces, 'bottom'])];
          }
          // Z-axis: back face hidden if not last Z, front face hidden if not first Z
          if (!zLast) {
            options.hiddenFaces = [...new Set([...options.hiddenFaces, 'back'])];
          }
          if (!zFirst) {
            options.hiddenFaces = [...new Set([...options.hiddenFaces, 'front'])];
          }

          // Hidden edges at internal boundaries
          // X-axis internal boundaries affect: top, bottom, front, back faces
          if (!xLast) {
            // Upper X boundary (right edge on visible faces)
            for (const face of ['top', 'bottom', 'front', 'back']) {
              if (!options.hiddenFaces.includes(face)) {
                if (!options.hiddenEdges[face]) options.hiddenEdges[face] = [];
                if (!options.hiddenEdges[face].includes('right')) {
                  options.hiddenEdges[face].push('right');
                }
              }
            }
          }
          if (!xFirst) {
            // Lower X boundary (left edge on visible faces)
            for (const face of ['top', 'bottom', 'front', 'back']) {
              if (!options.hiddenFaces.includes(face)) {
                if (!options.hiddenEdges[face]) options.hiddenEdges[face] = [];
                if (!options.hiddenEdges[face].includes('left')) {
                  options.hiddenEdges[face].push('left');
                }
              }
            }
          }

          // Y-axis internal boundaries affect: front, back, left, right faces
          if (!yLast) {
            // Upper Y boundary (top edge of side faces)
            for (const face of ['front', 'back', 'left', 'right']) {
              if (!options.hiddenFaces.includes(face)) {
                if (!options.hiddenEdges[face]) options.hiddenEdges[face] = [];
                if (!options.hiddenEdges[face].includes('top')) {
                  options.hiddenEdges[face].push('top');
                }
              }
            }
          }
          if (!yFirst) {
            // Lower Y boundary (bottom edge of side faces)
            for (const face of ['front', 'back', 'left', 'right']) {
              if (!options.hiddenFaces.includes(face)) {
                if (!options.hiddenEdges[face]) options.hiddenEdges[face] = [];
                if (!options.hiddenEdges[face].includes('bottom')) {
                  options.hiddenEdges[face].push('bottom');
                }
              }
            }
          }

          // Z-axis internal boundaries affect: top/bottom faces AND left/right faces
          if (!zLast) {
            // Upper Z boundary (back edge)
            // On top/bottom: back edge = CSS border-top
            for (const face of ['top', 'bottom']) {
              if (!options.hiddenFaces.includes(face)) {
                if (!options.hiddenEdges[face]) options.hiddenEdges[face] = [];
                if (!options.hiddenEdges[face].includes('top')) {
                  options.hiddenEdges[face].push('top');
                }
              }
            }
            // On left/right: back edge = CSS border-right
            for (const face of ['left', 'right']) {
              if (!options.hiddenFaces.includes(face)) {
                if (!options.hiddenEdges[face]) options.hiddenEdges[face] = [];
                if (!options.hiddenEdges[face].includes('right')) {
                  options.hiddenEdges[face].push('right');
                }
              }
            }
          }
          if (!zFirst) {
            // Lower Z boundary (front edge)
            // On top/bottom: front edge = CSS border-bottom
            for (const face of ['top', 'bottom']) {
              if (!options.hiddenFaces.includes(face)) {
                if (!options.hiddenEdges[face]) options.hiddenEdges[face] = [];
                if (!options.hiddenEdges[face].includes('bottom')) {
                  options.hiddenEdges[face].push('bottom');
                }
              }
            }
            // On left/right: front edge = CSS border-left
            for (const face of ['left', 'right']) {
              if (!options.hiddenFaces.includes(face)) {
                if (!options.hiddenEdges[face]) options.hiddenEdges[face] = [];
                if (!options.hiddenEdges[face].includes('left')) {
                  options.hiddenEdges[face].push('left');
                }
              }
            }
          }

          // Mark as fragment
          options.isFragment = true;
          options.parentKey = originalParentKey;

          // Propagate faceGroups
          if (this.faceGroups) {
            options.faceGroups = { ...this.faceGroups };
          }

          fragments.push(new Cuboid(options));
        }
      }
    }

    return fragments.length > 0 ? fragments : null;
  }

  /**
   * Compute a distance value for sorting cuboids based on camera quadrant.
   * @param {number} quadrant - Camera quadrant (0-3).
   * @return {number} Distance value.
   */
  getDistanceValue(quadrant) {
    switch (quadrant) {
      case 0:
        return this.x - this.z; // Positive Z is away, so subtract
      case 1:
        return this.x + this.z; // Positive Z is away
      case 2:
        return -(this.x - this.z); // Negative of quadrant 0
      case 3:
        return -(this.x + this.z); // Negative of quadrant 1
      default:
        return 0;
    }
  }

  /**
   * Check if the camera state has changed
   * @param {Camera} camera - The current camera instance
   * @return {boolean} True if the camera state has changed
   */
  hasCameraChanged(camera) {
    return (
      this.lastCameraState.angle !== camera.angle ||
      this.lastCameraState.skewAngle !== camera.skewAngle ||
      this.lastCameraState.position.x !== camera.position.x ||
      this.lastCameraState.position.y !== (camera.position.y || 0) ||
      this.lastCameraState.position.z !== camera.position.z ||
      this.lastCameraState.zoomLevel !== camera.zoomLevel
    );
  }

  /**
   * Check if this cuboid has any non-opaque faces (opacity < 1 or glass effect).
   * Used to determine if backface culling should be disabled.
   * @return {boolean} True if any face is non-opaque
   */
  hasNonOpaqueFace() {
    for (const face of Object.keys(this.faceElements)) {
      const material = this.materials[face];
      if (!material) continue;

      // Check opacity
      if (material.opacity < 1) return true;

      // Check for glass effect (inherently transparent)
      if (material.type === 'special' && material.effectType === 'glass') return true;
    }
    return false;
  }

  /**
   * Check if this cuboid should not block movement (e.g., water, gas).
   * Returns true if noCollision is set directly on the cuboid,
   * or if ALL faces have materials with noCollision set.
   * @return {boolean} True if cuboid should not collide
   */
  hasNoCollision() {
    // Direct property takes precedence
    if (this.noCollision) return true;

    // Check if ALL faces have noCollision materials
    const faces = Object.keys(this.materials);
    if (faces.length === 0) return false;

    return faces.every(face => {
      const material = this.materials[face];
      return material && material.noCollision;
    });
  }

  /**
   * Get the set of backfacing faces based on camera angle and skew.
   * Backfacing faces are those whose normals point away from the camera.
   * @param {Camera} camera - The current camera instance
   * @return {Set<string>} Set of backfacing face names
   */
  getBackfacingFaces(camera) {
    const backfacing = new Set();
    const angleRad = camera.angle * MathUtils.DEG_TO_RAD;
    const cosAngle = Math.cos(angleRad);
    const sinAngle = Math.sin(angleRad);

    // Top/bottom based on skew
    if (camera.skewAngle >= 0) {
      backfacing.add('bottom'); // Viewing from above, bottom faces away
    } else {
      backfacing.add('top'); // Viewing from below, top faces away
    }

    // Side faces based on angle (using dot product of normal with view direction)
    // View direction is approximately (-sin(angle), 0, -cos(angle))
    // Face is backfacing when dot(normal, viewDir) > 0

    // Right face normal: (1, 0, 0), dot = -sin(angle)
    // Backfacing when sin(angle) < 0, i.e., 180-360°
    if (sinAngle < 0) backfacing.add('right');

    // Left face normal: (-1, 0, 0), dot = sin(angle)
    // Backfacing when sin(angle) > 0, i.e., 0-180°
    if (sinAngle > 0) backfacing.add('left');

    // Front face normal: (0, 0, 1), dot = -cos(angle)
    // Backfacing when cos(angle) < 0, i.e., 90-270°
    if (cosAngle < 0) backfacing.add('front');

    // Back face normal: (0, 0, -1), dot = cos(angle)
    // Backfacing when cos(angle) > 0, i.e., 0-90° or 270-360°
    if (cosAngle > 0) backfacing.add('back');

    return backfacing;
  }

  /**
   * Calculate seam fix extension based on camera angle and skew.
   * Grows the cuboid as a 3D shape away from camera, keeping camera-facing edges fixed.
   * Uses smooth trig-based interpolation so extension tapers to 0 when faces are edge-on.
   *
   * @param {Camera} camera - The current camera instance.
   * @return {Object} Extension offsets {posX, posY, posZ, extWidth, extHeight, extDepth}
   */
  calculateSeamExtension(camera) {
    const EXTENSION = 3.0; // pixels

    const angleRad = camera.angle * MathUtils.DEG_TO_RAD;
    const cosAngle = Math.cos(angleRad);
    const sinAngle = Math.sin(angleRad);
    const absCos = Math.abs(cosAngle);
    const absSin = Math.abs(sinAngle);

    // X extension: based on sin(angle)
    // sin > 0 (0-180°): right face more visible, extend left
    // sin < 0 (180-360°): left face more visible, extend right
    // Tapers to 0 at 0° and 180° (left/right faces edge-on)
    const posOffsetX = -EXTENSION * Math.max(0, sinAngle);
    const extWidth = EXTENSION * absSin;

    // Z extension: based on cos(angle)
    // cos > 0 (315-45°): front face more visible, extend back
    // cos < 0 (135-225°): back face more visible, extend front
    // Tapers to 0 at 90° and 270° (front/back faces edge-on)
    const posOffsetZ = EXTENSION * Math.max(0, cosAngle);
    const extDepth = EXTENSION * absCos;

    // Y extension: based on yScale (derived from skewAngle)
    // Tapers to 0 at skew=0 (horizontal view, top/bottom edge-on)
    const yScale = Math.sin(camera.skewAngle * Math.PI / 4);
    const absYScale = Math.abs(yScale);
    const posOffsetY = -EXTENSION * Math.max(0, yScale);
    const extHeight = EXTENSION * absYScale;

    return {
      posX: posOffsetX,
      posY: posOffsetY,
      posZ: posOffsetZ,
      extWidth: extWidth,
      extHeight: extHeight,
      extDepth: extDepth
    };
  }

  /**
   * Update the cuboid's appearance based on the current camera.
   * @param {Camera} camera - The current camera instance.
   */
  update(camera) {
    // Check if this cuboid has the selected face
    // For fragments, also match against parent key
    const myKey = this.getKey();
    const effectiveKeyForSelection = this.isFragment ? this.parentKey : myKey;
    const isCurrentlySelected = Cuboid.selectedFace &&
                               Cuboid.selectedFace.cuboidKey === effectiveKeyForSelection;
    
    // Check if selection state has changed (including global selection version for group highlights)
    const selectionVersionChanged = this.lastSelectionVersion !== Cuboid.selectionVersion;
    const selectionChanged = selectionVersionChanged ||
                             this.isSelected !== isCurrentlySelected ||
                             (isCurrentlySelected && this.lastSelectedFace !== Cuboid.selectedFace.face);
    
    // Update selection state
    this.isSelected = isCurrentlySelected;
    this.lastSelectedFace = isCurrentlySelected ? Cuboid.selectedFace.face : null;
    
    // Check if camera has changed
    const cameraChanged = this.hasCameraChanged(camera);
    
    // Determine if we need to update transforms
    const needsTransformUpdate = this.needsUpdate || cameraChanged || selectionChanged;
    
    // Determine if we need to update face ordering
    const normRotation = ((camera.angle % 360 + 360) % 360);
    const currentQuadrant = Math.floor(normRotation / 90);
    const viewingFromBelow = camera.skewAngle < 0;
    const quadrantChanged = this.lastQuadrant !== currentQuadrant || this.lastViewingFromBelow !== viewingFromBelow;
    
    // If nothing has changed, we can skip the update
    if (!needsTransformUpdate && !quadrantChanged && !selectionChanged) {
      return;
    }
    
    // Update the last camera state
    this.lastCameraState = {
      angle: camera.angle,
      skewAngle: camera.skewAngle,
      position: { x: camera.position.x, y: camera.position.y || 0, z: camera.position.z },
      zoomLevel: camera.zoomLevel
    };

    // Calculate full dimensions (in pixels) - only if needed
    // On Android: bake zoom into dimensions to fix clipping issues with CSS transforms
    // On Safari/other: apply zoom via CSS scale on world element to prevent re-rasterization
    if (needsTransformUpdate) {
      const zoom = camera.zoomLevel;
      const isAndroid = DOMUtils.isAndroid();
      const cubeSize = isAndroid ? this.cubeSize * zoom : this.cubeSize;

      // Store zoom for materials to use (they need to scale textures/patterns accordingly)
      // Track zoom changes for Android material refresh (done later, after face sizes are set)
      const zoomChanged = this.currentZoom !== zoom;
      this.currentZoom = zoom;

      const fullWidth = this.width * cubeSize;
      const fullHeight = this.height * cubeSize;
      const fullDepth = this.depth * cubeSize;

      // Seam fix: extend geometry away from camera to hide CSS rendering gaps
      const ext = this.calculateSeamExtension(camera);
      const renderWidth = fullWidth + ext.extWidth;
      const renderHeight = fullHeight + ext.extHeight;
      const renderDepth = fullDepth + ext.extDepth;

      // Compute yScale early (needed for face expansion calculation)
      // yScale represents the vertical projection factor based on camera skew
      const yScale = Transform.calculateYScale(camera.skewAngle);

      // Screen-space face expansion: grow each face in the direction AWAY from camera
      // to hide remaining seams/holes at near edges (in addition to 3D cuboid grow)
      const FACE_EXPAND = 0.0; // pixels-tunable

      // Calculate which direction is "away from camera" for expansion
      // Using sin/cos of camera angle to determine world-space "away" directions
      const angleRad = camera.angle * MathUtils.DEG_TO_RAD;
      const sinAngle = Math.sin(angleRad);
      const cosAngle = Math.cos(angleRad);
      const absSin = Math.abs(sinAngle);
      const absCos = Math.abs(cosAngle);

      // Expansion factors for each world direction (0 when toward camera, positive when away)
      // These determine how much to expand in each direction based on camera orientation
      //
      // Coordinate system analysis:
      // - worldZ = -gameZ (negated), so higher gameZ = lower worldZ
      // - At angle 0° (cos>0): camera looks from front, away = +gameZ = -worldZ
      // - At angle 180° (cos<0): camera looks from back, away = -gameZ = +worldZ
      // - At angle 90° (sin>0): camera looks from right, away = -X
      // - At angle 270° (sin<0): camera looks from left, away = +X
      const expandWorldPosX = Math.max(0, -sinAngle) * FACE_EXPAND; // +X away when sin < 0 (viewing from left)
      const expandWorldNegX = Math.max(0, sinAngle) * FACE_EXPAND;  // -X away when sin > 0 (viewing from right)
      const expandWorldPosZ = Math.max(0, -cosAngle) * FACE_EXPAND; // +worldZ away when cos < 0 (viewing from back)
      const expandWorldNegZ = Math.max(0, cosAngle) * FACE_EXPAND;  // -worldZ away when cos > 0 (viewing from front)
      const expandWorldPosY = Math.max(0, -yScale) * FACE_EXPAND;   // +Y away when yScale < 0 (viewing from below)
      const expandWorldNegY = Math.max(0, yScale) * FACE_EXPAND;    // -Y away when yScale > 0 (viewing from above)

      // Per-face expansion (mapping element coords to world coords):
      // Top face: elem X = world X, elem Y = game Z = -world Z
      //   So: expand -worldZ → expand +elem Y, expand +worldZ → expand -elem Y
      // Bottom face: same dims but transform has negative scaleY (flips visual Y)
      // Front/back: elem X = world X, elem Y = -world Y (CSS Y goes down)
      // Left/right: elem X = game Z = -world Z, elem Y = -world Y

      // Store per-face expansion data for use in transforms
      const sideXWidth = expandWorldPosX + expandWorldNegX;
      // For left/right: elemX = -worldZ, so expandWorldNegZ → +elemX expansion
      const sideZWidth = expandWorldPosZ + expandWorldNegZ;
      const sideHeight = expandWorldPosY + expandWorldNegY;

      this._faceExpansion = {
        // Top face: center transform-origin, elemX = worldX, elemY = gameZ = -worldZ
        // IMPORTANT: CSS rotation is CLOCKWISE for positive angles, not counter-clockwise!
        // This changes the rotation formula from standard math convention.
        // CSS rotate(θ): (x,y) → (x·cos + y·sin, -x·sin + y·cos)
        //
        // With center origin and expansion (dW, dH):
        // - Center shifts by (dW/2, dH/2) in CSS coords
        // - Content shifts by CSS_transform(-dW/2, -dH/2) relative to new center
        // Movement = (dW/2, dH/2) + CSS_transform(-dW/2, -dH/2)
        //
        // Using CSS rotation formula (note the + sign before sin terms):
        // posOffsetX = -sideXWidth/2 * (1 - cos) + sideZWidth/2 * sin
        // posOffsetY = -sideZWidth/2 - (sideXWidth*sin - sideZWidth*cos)/2 * yScale
        top: {
          width: sideXWidth,
          height: sideZWidth,
          translateX: 0,
          translateY: 0,
          posOffsetX: -sideXWidth / 2 * (1 + absCos) + sideZWidth / 2 * absSin,
          posOffsetY: -sideZWidth / 2 * (1 + absCos * yScale) - sideXWidth / 2 * absSin * yScale
        },
        // Bottom face: transform is scaleY(-yScale) rotate(-angle)
        // With -angle: sin(-angle) = -sin, cos(-angle) = cos
        // CSS rotate(-angle): (x,y) → (x*cos - y*sin, x*sin + y*cos)
        // Then scaleY(-yScale) flips and scales the y component
        // For content shift (-dW/2, -dH/2):
        //   After rotate: (-dW*cos/2 + dH*sin/2, -dW*sin/2 - dH*cos/2)
        //   After scaleY(-yScale): (same_x, (dW*sin/2 + dH*cos/2) * yScale)
        // Movement = center_shift + transform_shift
        bottom: {
          width: sideXWidth,
          height: sideZWidth,
          translateX: 0,
          translateY: 0,
          posOffsetX: -sideXWidth / 2 * (1 + absCos) - sideZWidth / 2 * absSin,
          posOffsetY: -sideZWidth / 2 * (1 + absCos * yScale) + sideXWidth / 2 * absSin * yScale
        },
        // Side faces: left-top origin - translate offsets for away-from-camera expansion
        front: {
          width: sideXWidth,
          height: sideHeight,
          translateX: -expandWorldNegX,
          translateY: -expandWorldPosY,
          posOffsetX: 0,
          posOffsetY: 0
        },
        back: {
          width: sideXWidth,
          height: sideHeight,
          // Back face is rotated 180° from front, so element coords are flipped
          // Use opposite translate to achieve same visual expansion direction
          translateX: -expandWorldPosX,
          translateY: -expandWorldPosY,
          posOffsetX: 0,
          posOffsetY: 0
        },
        left: {
          width: sideZWidth,
          height: sideHeight,
          // Left face (Red) - use NegZ instead of PosZ
          translateX: -expandWorldNegZ,
          translateY: -expandWorldPosY,
          posOffsetX: 0,
          posOffsetY: 0
        },
        right: {
          width: sideZWidth,
          height: sideHeight,
          translateX: -expandWorldPosZ,
          translateY: -expandWorldPosY,
          posOffsetX: 0,
          posOffsetY: 0
        }
      };

      // Update face sizes based on directional expansion
      Object.entries(this.faceElements).forEach(([face, faceEl]) => {
        if (!faceEl) return; // Skip hidden faces
        const exp = this._faceExpansion[face];
        if (face === 'top' || face === 'bottom') {
          faceEl.style.width = `${renderWidth + exp.width}px`;
          faceEl.style.height = `${renderDepth + exp.height}px`;
        } else if (face === 'front' || face === 'back') {
          faceEl.style.width = `${fullWidth + exp.width}px`;
          faceEl.style.height = `${renderHeight + exp.height}px`;
        } else if (face === 'left' || face === 'right') {
          faceEl.style.width = `${fullDepth + exp.width}px`;
          faceEl.style.height = `${renderHeight + exp.height}px`;
        }
      });

      // Android material refresh - MUST happen after face sizes are set above
      // Otherwise materials are applied with wrong dimensions
      if (isAndroid && zoomChanged && this._materialsInitialized) {
        this.refreshMaterials();
        // Clear AO overlay texture tracking so styles get re-applied
        // (refreshMaterials preserves DOM elements but we need to ensure correct styling)
        if (this._aoOverlays) {
          Object.values(this._aoOverlays).forEach(overlay => {
            if (overlay) overlay._textureURL = null;
          });
        }
      }
      if (isAndroid && !this._materialsInitialized) {
        this._materialsInitialized = true;
        this.refreshMaterials();
        // Clear AO overlay texture tracking so styles get re-applied
        if (this._aoOverlays) {
          Object.values(this._aoOverlays).forEach(overlay => {
            if (overlay) overlay._textureURL = null;
          });
        }
      }

      // Compute world coordinates (zoom applied via CSS scale on world element)
      // The key change: Adjust Z so that positive Z goes away from camera
      // This means the front face is at -Z (toward camera) and back face is at -(Z+depth)
      // Apply seam extension position offsets to shift geometry away from camera
      const worldX = this.x * cubeSize + ext.posX;
      const worldY = this.y * cubeSize + ext.posY;
      const worldZ = -this.z * cubeSize + ext.posZ; // Negate Z so positive Z is away from camera

      // Compute camera rotation using Transform utility
      const rotation = Transform.getCameraRotation(camera);
      const cos = rotation.cos;
      const sin = rotation.sin;
      // Camera position: scale by zoom on Android (baked), otherwise raw (CSS scale handles zoom)
      const camPosX = isAndroid ? camera.position.x * zoom : camera.position.x;
      const camPosZ = isAndroid ? camera.position.z * zoom : camera.position.z;
      const relX = worldX - camPosX;
      const relZ = worldZ - camPosZ;

      // absYScale for proper projection (no stretching beyond 1:1)
      // yScale was computed earlier for face expansion
      const absYScale = Math.abs(yScale);

      // Base coordinates in camera space.
      const baseX = relX * cos - relZ * sin;
      const baseZ = (relX * sin + relZ * cos) * yScale;

      // Compute top-layer center (world space) using render dimensions for seam fix.
      const topLayerCenterWorldX = worldX + renderWidth / 2;
      const topLayerCenterWorldY = worldY + renderHeight; // Top of the cuboid
      const topLayerCenterWorldZ = worldZ - renderDepth / 2; // Center between front and back (adjusted for Z direction)

      // Rotate the top-layer center relative to camera (uses camPosX/Z which are scaled on Android).
      const relCenterX = topLayerCenterWorldX - camPosX;
      const relCenterZ = topLayerCenterWorldZ - camPosZ;
      const rotatedCenterX = relCenterX * cos - relCenterZ * sin;
      const rotatedCenterZ = (relCenterX * sin + relCenterZ * cos) * yScale;

      // Effective center for screen positioning.
      // Apply camera.position.y offset to keep followed target vertically centered
      // On Android, scale cameraY by zoom since all positions are in zoomed space
      const cameraY = isAndroid ? (camera.position.y || 0) * zoom : (camera.position.y || 0);
      const effectiveCenterX = rotatedCenterX;
      const effectiveCenterY = rotatedCenterZ - (topLayerCenterWorldY - cubeSize / 2) + cameraY;

      // Calculate the top face's top-left corner using render dimensions.
      const topFaceTLX = effectiveCenterX - renderWidth / 2;
      const topFaceTLY = effectiveCenterY - renderDepth / 2;

      // Compute transforms for the top and bottom faces.
      // Use absYScale for proper projection (no stretching beyond 1:1)
      // Sign determines whether viewing from above or below
      // Note: We use left/top for positioning to help mobile browsers calculate correct bounding boxes
      // The transform only contains scale/rotate/skew, not translate
      const topTransform = `scaleY(${yScale}) rotate(${camera.angle}deg)`;
      const bottomTransform = `scaleY(${-yScale}) rotate(${-camera.angle}deg)`;

      // Compute side face transforms using render dimensions.
      const centerX = effectiveCenterX;
      const centerY = effectiveCenterY;
      const halfW = renderWidth / 2;
      const halfD = renderDepth / 2;

      // Right face corners.
      const rightCorner1X = centerX + (halfW * cos - halfD * sin);
      const rightCorner1Y = centerY + (halfW * sin * yScale + halfD * cos * yScale);
      const rightCorner2X = centerX + (halfW * cos + halfD * sin);
      const rightCorner2Y = centerY + (halfW * sin * yScale - halfD * cos * yScale);
      // Left face corners.
      const leftCorner1X = centerX - (halfW * cos + halfD * sin);
      const leftCorner1Y = centerY - (halfW * sin * yScale - halfD * cos * yScale);
      const leftCorner2X = centerX - (halfW * cos - halfD * sin);
      const leftCorner2Y = centerY - (halfW * sin * yScale + halfD * cos * yScale);
      // Front face corners.
      const frontCorner1X = centerX - (halfW * cos + halfD * sin);
      const frontCorner1Y = centerY - (halfW * sin * yScale - halfD * cos * yScale);
      const frontCorner2X = centerX + (halfW * cos - halfD * sin);
      const frontCorner2Y = centerY + (halfW * sin * yScale + halfD * cos * yScale);
      // Back face corners.
      const backCorner1X = centerX - (halfW * cos - halfD * sin);
      const backCorner1Y = centerY - (halfW * sin * yScale + halfD * cos * yScale);
      const backCorner2X = centerX + (halfW * cos + halfD * sin);
      const backCorner2Y = centerY + (halfW * sin * yScale - halfD * cos * yScale);

      // Compute scaling factors
      const rightScale = (rightCorner2X - rightCorner1X) / (cubeSize * this.depth);
      const leftScale = (leftCorner1X - leftCorner2X) / (cubeSize * this.depth);
      const frontScale = (frontCorner2X - frontCorner1X) / (cubeSize * this.width);
      const backScale = (backCorner1X - backCorner2X) / (cubeSize * this.width);

      // Cache expensive atan2 calculations (only when camera changes)
      if (!this.cachedAngles || cameraChanged) {
        const RAD_TO_DEG = 180 / Math.PI;
        this.cachedAngles = {
          right: Math.atan2(rightCorner2Y - rightCorner1Y, rightCorner2X - rightCorner1X) * RAD_TO_DEG,
          left: Math.atan2(leftCorner1Y - leftCorner2Y, leftCorner1X - leftCorner2X) * RAD_TO_DEG,
          front: Math.atan2(frontCorner2Y - frontCorner1Y, frontCorner2X - frontCorner1X) * RAD_TO_DEG,
          back: Math.atan2(backCorner1Y - backCorner2Y, backCorner1X - backCorner2X) * RAD_TO_DEG
        };
      }

      // Cache the computed transforms
      // Include per-face translate to offset expansion in the "away from camera" direction
      // This is applied FIRST (rightmost in CSS transform chain) before scale/skew/rotate
      const exp = this._faceExpansion;
      this.computedTransforms = {
        top: `${topTransform} translate(${exp.top.translateX}px, ${exp.top.translateY}px)`,
        bottom: `${bottomTransform} translate(${exp.bottom.translateX}px, ${exp.bottom.translateY}px)`,
        right: `skewY(${this.cachedAngles.right}deg) scaleX(${rightScale}) translate(${exp.right.translateX}px, ${exp.right.translateY}px)`,
        left: `skewY(${this.cachedAngles.left}deg) scaleX(${leftScale}) translate(${exp.left.translateX}px, ${exp.left.translateY}px)`,
        front: `skewY(${this.cachedAngles.front}deg) scaleX(${frontScale}) translate(${exp.front.translateX}px, ${exp.front.translateY}px)`,
        back: `skewY(${this.cachedAngles.back}deg) scaleX(${backScale}) translate(${exp.back.translateX}px, ${exp.back.translateY}px)`
      };

      // Store positions for all faces (for left/top CSS positioning)
      // Include position offsets from expansion to keep transform-origins correct
      this.computedPositions = {
        top: { x: topFaceTLX + exp.top.posOffsetX, y: topFaceTLY + exp.top.posOffsetY },
        bottom: { x: topFaceTLX + exp.bottom.posOffsetX, y: topFaceTLY + renderHeight + exp.bottom.posOffsetY },
        right: { x: rightCorner1X + exp.right.posOffsetX, y: rightCorner1Y + exp.right.posOffsetY },
        left: { x: leftCorner2X + exp.left.posOffsetX, y: leftCorner2Y + exp.left.posOffsetY },
        front: { x: frontCorner1X + exp.front.posOffsetX, y: frontCorner1Y + exp.front.posOffsetY },
        back: { x: backCorner2X + exp.back.posOffsetX, y: backCorner2Y + exp.back.posOffsetY }
      };
    }

    // No CSS scale transform on individual cuboids
    // On Safari: zoom is applied via CSS scale on #world element (prevents re-rasterization)
    // On Android: zoom is baked into dimensions above (fixes clipping issues)
    this.element.style.transform = '';

    // Determine face ordering based on camera angle and whether viewing from above or below
    // Side face order stays the same based on quadrant, only top/bottom swap positions
    let faceOrder;

    if (viewingFromBelow) {
      // When viewing from below: top is hidden (first), bottom is visible (last)
      // Side face order remains the same as from above
      switch (currentQuadrant) {
        case 0:
          faceOrder = ['top', 'back', 'left', 'front', 'right', 'bottom'];
          break;
        case 1:
          faceOrder = ['top', 'left', 'front', 'right', 'back', 'bottom'];
          break;
        case 2:
          faceOrder = ['top', 'front', 'right', 'back', 'left', 'bottom'];
          break;
        case 3:
          faceOrder = ['top', 'right', 'back', 'front', 'left', 'bottom'];
          break;
        default:
          faceOrder = ['top', 'back', 'left', 'front', 'right', 'bottom'];
      }
    } else {
      // Normal view from above: bottom is hidden (first), top is visible (last)
      switch (currentQuadrant) {
        case 0:
          faceOrder = ['bottom', 'back', 'left', 'front', 'right', 'top'];
          break;
        case 1:
          faceOrder = ['bottom', 'left', 'front', 'right', 'back', 'top'];
          break;
        case 2:
          faceOrder = ['bottom', 'front', 'right', 'back', 'left', 'top'];
          break;
        case 3:
          faceOrder = ['bottom', 'right', 'back', 'front', 'left', 'top'];
          break;
        default:
          faceOrder = ['bottom', 'back', 'left', 'front', 'right', 'top'];
      }
    }

    // --- Backface Culling ---
    // Cull backfacing faces unless cuboid has transparent/non-opaque faces
    const shouldCull = !this.hasNonOpaqueFace();
    const backfacingFaces = shouldCull ? this.getBackfacingFaces(camera) : new Set();

    // --- Flat Shading Implementation ---
    // Only recalculate shading if transforms have changed
    if (needsTransformUpdate) {
      // Use pre-normalized sun vector from global cache
      const normalizedSun = SunVector.getNormalized();
      const intensity = SunVector.getIntensity();

      // Check if sun has changed since last calculation
      const sunKey = `${normalizedSun.x},${normalizedSun.y},${normalizedSun.z},${intensity}`;
      const sunChanged = this.lastSunKey !== sunKey;

      // Cache brightness calculations when sun hasn't changed
      if (sunChanged || !this.cachedBrightness) {
        this.cachedBrightness = {};
        const dot = (a, b) => a.x * b.x + a.y * b.y + a.z * b.z;

        // Pre-calculate brightness for all faces
        Object.keys(Cuboid.FACE_NORMALS).forEach(face => {
          const normal = Cuboid.FACE_NORMALS[face];
          let diffuse = dot(normal, normalizedSun);
          diffuse = Math.max(0, diffuse);
          this.cachedBrightness[face] = 1 + (1 - intensity) * 2 * (diffuse - 0.5);
        });

        this.lastSunKey = sunKey;
      }

      // Apply transforms, positions, and cached shading to all faces
      // Use left/top for positioning (helps mobile browsers calculate correct bounding boxes)
      // Use batched style updates to reduce layout thrashing
      faceOrder.forEach(face => {
        const faceEl = this.faceElements[face];
        if (!faceEl) return;

        // Backface culling: hide faces pointing away from camera
        if (backfacingFaces.has(face)) {
          faceEl.style.visibility = 'hidden';
          return;
        }
        faceEl.style.visibility = 'visible';

        // Use cached brightness value
        const brightness = this.cachedBrightness[face] || 1;
        const pos = this.computedPositions[face];

        // Face expansion offset is now handled via translate() in the transform
        // (applied in local space before scale/skew/rotate)

        // Batch style updates - single DOM write
        // Note: Using shading overlay instead of filter: brightness() for Safari/iOS compatibility
        // Large 3D-transformed elements with CSS filters cause rendering failures on Safari
        Object.assign(faceEl.style, {
          transform: this.computedTransforms[face],
          left: `${pos.x}px`,
          top: `${pos.y}px`
        });

        // Apply shading via overlay (Safari-compatible alternative to brightness filter)
        this.applyShadingOverlay(face, brightness);
      });
    }
    // --- End Flat Shading Implementation ---

    // --- Ambient Occlusion ---
    // Apply AO independently of transform updates to ensure overlays persist after material refresh
    // Skip AO for debug/overlay cuboids (e.g., character collider visualization)
    if (AmbientOcclusion.enabled && !this.skipAO) {
      // In dungeon precalc mode, use non-blocking AO: return cached texture immediately,
      // queue recalculation in background if not yet precalculated.
      const nonBlocking = true;
      Object.keys(this.faceElements).forEach(face => {
        const faceEl = this.faceElements[face];
        if (!faceEl) return;
        const aoTexture = AmbientOcclusion.getTextureForFace(this, face, nonBlocking);
        if (aoTexture) {
          this.applyAOTexture(face, aoTexture);
        }
      });
    } else {
      // Remove AO overlays when disabled
      Object.keys(this.faceElements).forEach(face => {
        if (this.hasAOTexture(face)) {
          this.removeAOTexture(face);
        }
      });
    }
    // --- End Ambient Occlusion ---

    // Apply/remove highlight styles for multi-selection and group highlighting
    if (selectionChanged || needsTransformUpdate) {
      // Update the selection version tracker
      this.lastSelectionVersion = Cuboid.selectionVersion;

      const selectedFaces = Cuboid.selectedFaces || new Set();
      const primarySelected = Cuboid.primarySelectedFace;
      const myKey = this.getKey();
      // For fragments, also check if selection matches parent key
      const effectiveKey = this.isFragment ? this.parentKey : myKey;

      // Get group manager if available
      const groupManager = window.game?.world?.materialGroupManager;

      // Find all group IDs that have a selected face
      const selectedGroupIds = new Set();
      if (groupManager) {
        for (const sel of selectedFaces) {
          const separatorIndex = sel.lastIndexOf('|');
          const selCuboidKey = sel.substring(0, separatorIndex);
          const selFace = sel.substring(separatorIndex + 1);
          const group = groupManager.getGroupForFace(selCuboidKey, selFace);
          if (group) {
            selectedGroupIds.add(group.id);
          }
        }
      }

      Object.entries(this.faceElements).forEach(([face, faceEl]) => {
        if (!faceEl) return; // Skip hidden faces

        // For fragments, check if the PARENT's face is selected
        const serializedFace = `${effectiveKey}|${face}`;
        const isFaceSelected = selectedFaces.has(serializedFace);
        const isPrimarySelected = primarySelected === serializedFace;

        // Check if this face is part of a group that has a selected face
        let isGroupHighlighted = false;
        if (groupManager && !isFaceSelected) {
          // Use effectiveKey for group lookup too
          const faceGroup = groupManager.getGroupForFace(effectiveKey, face);
          if (faceGroup && selectedGroupIds.has(faceGroup.id)) {
            isGroupHighlighted = true;
          }
        }

        // Apply appropriate highlight style
        // Use batched style updates to reduce layout thrashing
        if (isFaceSelected) {
          // Direct selection: red highlight, but preserve neon glow if material is neon
          const material = this.materials[face];
          const isNeon = material && material.type === 'special' && material.effectType === 'neon';

          let boxShadow;
          if (isNeon) {
            const neonColor = material.color || '#3498db';
            const selectionShadow = isPrimarySelected
              ? 'inset 0 0 100px 20px rgba(255, 50, 50, 0.45)'
              : 'inset 0 0 50px 10px rgba(255, 50, 50, 0.25)';
            boxShadow = `0 0 10px ${neonColor}, 0 0 20px ${neonColor}, 0 0 30px ${neonColor}, ${selectionShadow}`;
          } else {
            boxShadow = isPrimarySelected
              ? 'inset 0 0 100px 20px rgba(255, 50, 50, 0.45)'
              : 'inset 0 0 50px 10px rgba(255, 50, 50, 0.25)';
          }

          Object.assign(faceEl.style, {
            outline: '2px solid rgba(255, 50, 50, 0.9)',
            outlineOffset: '-2px',
            boxShadow: boxShadow
          });
        } else if (isGroupHighlighted) {
          // Group member highlight: blue/cyan, but preserve neon glow if material is neon
          const material = this.materials[face];
          const isNeon = material && material.type === 'special' && material.effectType === 'neon';

          if (isNeon) {
            const neonColor = material.color || '#3498db';
            Object.assign(faceEl.style, {
              outline: '2px solid rgba(50, 150, 255, 0.7)',
              outlineOffset: '-2px',
              boxShadow: `0 0 10px ${neonColor}, 0 0 20px ${neonColor}, 0 0 30px ${neonColor}, inset 0 0 10px ${neonColor}`
            });
          } else {
            Object.assign(faceEl.style, {
              outline: '2px solid rgba(50, 150, 255, 0.7)',
              outlineOffset: '-2px',
              boxShadow: 'inset 0 0 30px 5px rgba(50, 150, 255, 0.2)'
            });
          }
        } else {
          // Clear highlight but preserve neon box-shadow if material is neon
          const material = this.materials[face];
          const isNeon = material && material.type === 'special' && material.effectType === 'neon';

          if (isNeon) {
            // Preserve neon glow
            const neonColor = material.color || '#3498db';
            Object.assign(faceEl.style, {
              outline: '',
              outlineOffset: '',
              boxShadow: `0 0 10px ${neonColor}, 0 0 20px ${neonColor}, 0 0 30px ${neonColor}, inset 0 0 10px ${neonColor}`
            });
          } else {
            Object.assign(faceEl.style, {
              outline: '',
              outlineOffset: '',
              boxShadow: ''
            });
          }
        }
      });
    }

    // Update face ordering if quadrant has changed OR if needsUpdate is true
    // (needsUpdate can be triggered by opacity changes which affect face visibility)
    if (quadrantChanged || this.needsUpdate) {
      this.lastQuadrant = currentQuadrant;
      this.lastViewingFromBelow = viewingFromBelow;

      // Check if any face has a webpage material (iframe) that shouldn't be moved
      // Moving iframes in the DOM causes them to reload
      const hasWebpageFace = Object.values(this.materials).some(
        mat => mat && mat.type === 'webpage'
      );

      if (!hasWebpageFace) {
        // Normal path: reorder faces via DOM manipulation
        const frag = document.createDocumentFragment();
        faceOrder.forEach(face => {
          const faceEl = this.faceElements[face];
          if (faceEl) {
            frag.appendChild(faceEl);
          }
        });
        this.element.append(frag);
      }
      // Webpage path: skip face reordering entirely to avoid iframe reload
      // Face order within the cuboid may be incorrect, but inter-cuboid depth sorting works
      // Note: z-index approach was removed because it created stacking contexts
      // that caused the webpage cuboid to appear above other cuboids
    }
    
    // Reset the update flag
    this.needsUpdate = false;
  }
}